import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import "./FoodRadarOrderForm.css";

const apiUrl = "/api";
const baseUrl = window.location.origin || "https://food.vakra.co.in";

const STEP_MODE = 1;
const STEP_DETAILS = 2;
const STEP_PRODUCTS = 3;
const STEP_REVIEW = 4;
const STEP_PAYMENT = 5;
const STEP_SUCCESS = 6;

const businessType = "fastfood";
const PLACEHOLDER_IMAGE = "https://via.placeholder.com/400x400?text=No+Image";

const CATEGORY_OPTIONS = [
  "All",
];

const safeArray = (value) => (Array.isArray(value) ? value : []);

const safeNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const safeFlag = (value, fallback = 0) => {
  if (value === true) return 1;
  if (value === false) return 0;
  if (value === 1 || value === 0) return Number(value);

  const text = String(value ?? "").trim().toLowerCase();
  if (["1", "true", "yes", "on", "enabled", "active"].includes(text)) return 1;
  if (["0", "false", "no", "off", "disabled", "inactive"].includes(text)) return 0;

  return fallback;
};

const normalizeMobile = (value) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, 10);


const getFoodRadarCustomerId = (customer = null) => {
  const rawId =
    customer?.id ??
    customer?.customer_id ??
    customer?.customerId ??
    customer?.food_radar_customer_id ??
    customer?.foodRadarCustomerId ??
    localStorage.getItem("foodRadarCustomerId") ??
    0;

  const id = Number(rawId || 0);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const roundDisplayAmount = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return 0;

  const sign = num < 0 ? -1 : 1;
  const absolute = Math.abs(num);
  const floorValue = Math.floor(absolute);
  const decimalPart = absolute - floorValue;

  return sign * (decimalPart > 0.3 ? floorValue + 1 : floorValue);
};

const formatMoney = (value) => `₹${roundDisplayAmount(value).toLocaleString("en-IN")}`;

const normalizeImage = (value) => {
  const img = Array.isArray(value) ? value[0] : value;

  if (!img) return PLACEHOLDER_IMAGE;

  if (String(img).startsWith("http://") || String(img).startsWith("https://")) {
    return img;
  }

  return `${baseUrl}${String(img).startsWith("/") ? "" : "/"}${img}`;
};

const parseJsonMaybe = (value, fallback = null) => {
  try {
    if (value === null || value === undefined || value === "") return fallback;
    if (Array.isArray(value) || typeof value === "object") return value;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};


const extractProductsFromApi = (responseData) => {
  const data = responseData || {};

  if (Array.isArray(data)) return data;
  if (Array.isArray(data.products)) return data.products;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.rows)) return data.rows;
  if (Array.isArray(data.data?.products)) return data.data.products;
  if (Array.isArray(data.data?.items)) return data.data.items;
  if (Array.isArray(data.data?.rows)) return data.data.rows;

  return [];
};

const extractOffersFromApi = (responseData) => {
  const data = responseData || {};

  if (Array.isArray(data)) return data;
  if (Array.isArray(data.offers)) return data.offers;

  if (
    Array.isArray(data.vendors) &&
    data.vendors.length > 0 &&
    Array.isArray(data.vendors[0]?.offers)
  ) {
    return data.vendors[0].offers;
  }

  if (
    Array.isArray(data.data?.vendors) &&
    data.data.vendors.length > 0 &&
    Array.isArray(data.data.vendors[0]?.offers)
  ) {
    return data.data.vendors[0].offers;
  }

  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.rows)) return data.rows;
  if (Array.isArray(data.data?.offers)) return data.data.offers;
  if (Array.isArray(data.data?.items)) return data.data.items;
  if (Array.isArray(data.data?.rows)) return data.data.rows;

  return [];
};


const normalizeOrderSettings = (raw = {}) => {
  const data = raw?.settings || raw?.data || raw || {};

  return {
    enable_dine_in: safeFlag(data.enable_dine_in, 1),
    enable_pickup: safeFlag(data.enable_pickup ?? data.enable_takeaway, 1),
    enable_home_delivery: safeFlag(data.enable_home_delivery ?? data.enable_delivery, 0),
    allow_cod: safeFlag(data.allow_cod, 1),
    allow_online_payment: safeFlag(data.allow_online_payment, 1),
    min_order_amount: safeNumber(data.min_order_amount ?? data.minimum_order_amount ?? data.minOrderAmount, 0),
    dine_in_min_amount: safeNumber(data.dine_in_min_amount ?? data.dineInMinAmount ?? data.dine_in_min_order_amount, 0),
    pickup_min_amount: safeNumber(data.pickup_min_amount ?? data.takeaway_min_amount ?? data.pickupMinAmount ?? data.takeawayMinAmount ?? data.pickup_min_order_amount, 0),
    delivery_min_amount: safeNumber(data.delivery_min_amount ?? data.deliveryMinAmount ?? data.delivery_min_order_amount, 0),
    delivery_fee: safeNumber(data.delivery_fee, 0),
    packaging_fee: safeNumber(data.packaging_fee, 0),
    delivery_radius_km: safeNumber(data.delivery_radius_km, 0),
    delivery_eta_min: safeNumber(data.delivery_eta_min, 0),
    delivery_eta_max: safeNumber(data.delivery_eta_max, 0),
  };
};

const normalizeProduct = (product = {}) => {
  const image = product.image_url || product.image || product.parentImage || "";
  const hasStockQuantity =
    product.stock_quantity !== null &&
    product.stock_quantity !== undefined &&
    product.stock_quantity !== "";

  return {
    ...product,
    id: Number(product.id || product.productId),
    productId: Number(product.id || product.productId),
    name: product.name || product.product_name || "Food Item",
    image_url: normalizeImage(image),
    category: product.category || "Others",
    description: product.description || "",
    food_type: product.food_type || product.foodType || "veg",
    trend_item: Number(product.trend_item || 0),
    is_active: Number(product.is_active ?? 1),
    availability: product.availability || product.status || "available",
    has_stock_quantity: hasStockQuantity,
    stock_quantity: hasStockQuantity ? Number(product.stock_quantity || 0) : 999999,
    selling_price: safeNumber(
      product.selling_price ?? product.price_show_to_customer ?? product.price,
      0
    ),
    price: safeNumber(product.price_show_to_customer ?? product.price ?? product.selling_price, 0),
    show_price: Number(product.show_price ?? 1),
    pricing_type: product.pricing_type || "fixed",
  };
};

const getFoodBadge = (foodType) => {
  const type = String(foodType || "veg").toLowerCase();

  if (type === "non_veg" || type === "nonveg" || type === "non-veg") {
    return { label: "🔴 Non-Veg", className: "nonveg" };
  }

  if (type === "egg") {
    return { label: "🟡 Egg", className: "egg" };
  }

  return { label: "🟢 Veg", className: "veg" };
};

const generateToken = () => String(Math.floor(1000 + Math.random() * 9000));

const generateRedeemCode = () => {
  const left = Math.random().toString(36).substring(2, 6).toUpperCase();
  const right = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${left}-${right}`;
};

const getExpiryDateString = () => {
  const expiry = new Date();
  expiry.setDate(expiry.getDate() + 7);
  return expiry.toISOString().slice(0, 19).replace("T", " ");
};

const parseCoordinate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const isValidLatitude = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num >= -90 && num <= 90;
};

const isValidLongitude = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num >= -180 && num <= 180;
};

const isUsableCoordinatePair = (lat, lng) => {
  const finalLat = Number(lat);
  const finalLng = Number(lng);

  if (!isValidLatitude(finalLat) || !isValidLongitude(finalLng)) return false;
  if (finalLat === 0 && finalLng === 0) return false;

  return true;
};

const calculateDistanceKm = (lat1, lon1, lat2, lon2) => {
  const fromLat = Number(lat1);
  const fromLon = Number(lon1);
  const toLat = Number(lat2);
  const toLon = Number(lon2);

  if (!isUsableCoordinatePair(fromLat, fromLon) || !isUsableCoordinatePair(toLat, toLon)) {
    return null;
  }

  const toRad = (value) => (Number(value) * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(toLat - fromLat);
  const dLon = toRad(toLon - fromLon);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(fromLat)) *
      Math.cos(toRad(toLat)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
};

const getOrderModeLabel = (mode) => {
  if (mode === "dine_in") return "Dine-in";
  if (mode === "delivery") return "Delivery";
  return "Takeaway";
};

const getAddressText = (addr = {}) => {
  return [
    addr.address_line1 || addr.address || addr.default_address || "",
    addr.address_line2 || "",
    addr.landmark || "",
    addr.city || "",
    addr.pincode || "",
  ]
    .filter(Boolean)
    .join(", ");
};

const getAddressPart = (address = {}, keys = []) => {
  for (const key of keys) {
    if (address[key]) return address[key];
  }

  return "";
};

const reverseGeocodeCurrentAddress = async (latitude, longitude) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
        latitude
      )}&lon=${encodeURIComponent(longitude)}`
    );

    if (!response.ok) return null;

    const data = await response.json();
    const address = data.address || {};

    const house = getAddressPart(address, ["house_number", "building", "amenity", "shop"]);
    const road = getAddressPart(address, ["road", "pedestrian", "footway", "neighbourhood"]);
    const area = getAddressPart(address, [
      "suburb",
      "neighbourhood",
      "quarter",
      "village",
      "town",
      "city_district",
    ]);
    const finalCity = getAddressPart(address, ["city", "town", "village", "municipality", "county"]);
    const finalState = address.state || "";
    const finalPincode = String(address.postcode || "").replace(/\D/g, "").slice(0, 6);
    const addressLine1 = [house, road].filter(Boolean).join(", ");

    return {
      display_name: data.display_name || "",
      address_line1: addressLine1 || data.display_name || "Current Work Location",
      address_line2: area || "",
      city: finalCity || "",
      state: finalState,
      pincode: finalPincode,
    };
  } catch (error) {
    console.error("Current address reverse geocode failed:", error);
    return null;
  }
};

const normalizeCustomerAddress = (addr = {}, fallbackLabel = "Profile") => {
  const label = addr.label || addr.address_label || addr.type || fallbackLabel;

  return {
    id: addr.id || `${String(label).toLowerCase().replace(/\s+/g, "-")}-address`,
    label,
    address_line1: addr.address_line1 || addr.address || addr.default_address || "",
    address_line2: addr.address_line2 || "",
    landmark: addr.landmark || "",
    city: addr.city || "",
    state: addr.state || addr.stateName || "",
    pincode: String(addr.pincode || "").replace(/\D/g, "").slice(0, 6),
    delivery_notes: addr.delivery_notes || addr.notes || "",
    latitude: parseCoordinate(addr.latitude ?? addr.customer_latitude ?? addr.lat),
    longitude: parseCoordinate(addr.longitude ?? addr.customer_longitude ?? addr.lng ?? addr.lon),
  };
};

const getCustomerProfileAddress = (customer = {}, addresses = []) => {
  const savedAddresses = safeArray(addresses);

  const preferred =
    savedAddresses.find((addr) => Number(addr.is_default || addr.isDefault) === 1) ||
    savedAddresses.find((addr) => {
      const label = String(addr.label || addr.address_label || addr.type || "").toLowerCase();
      return label.includes("home") || label.includes("profile");
    }) ||
    savedAddresses[0] ||
    null;

  if (preferred) return normalizeCustomerAddress(preferred, "Profile Address");

  const customerAddress = {
    label: "Profile Address",
    address_line1:
      customer.default_address ||
      customer.address_line1 ||
      customer.address ||
      customer.liveLocation ||
      customer.live_location ||
      "",
    address_line2: customer.address_line2 || customer.area || "",
    city: customer.city || "",
    state: customer.state || "",
    pincode: customer.pincode || "",
    latitude: customer.latitude || customer.customer_latitude || customer.lat || "",
    longitude: customer.longitude || customer.customer_longitude || customer.lng || customer.lon || "",
  };

  return getAddressText(customerAddress) || customerAddress.latitude
    ? normalizeCustomerAddress(customerAddress, "Profile Address")
    : null;
};

const getWorkAddress = (addresses = []) => {
  const found = safeArray(addresses).find((addr) => {
    const label = String(addr.label || addr.address_label || addr.type || "").toLowerCase();
    return label.includes("work") || label.includes("office");
  });

  return found ? normalizeCustomerAddress(found, "Work Address") : null;
};

const normalizeVendorProfilePayload = (raw = {}) => {
  const data = raw?.profile || raw?.vendor || raw?.user || raw?.data || raw || {};

  return {
    ...data,
    shop_latitude:
      parseCoordinate(data.shop_latitude ?? data.shopLatitude ?? data.shop_lat ?? data.latitude ?? data.lat),
    shop_longitude:
      parseCoordinate(data.shop_longitude ?? data.shopLongitude ?? data.shop_lng ?? data.shop_lon ?? data.longitude ?? data.lng ?? data.lon),
  };
};


const playCoinKhanSound = () => {
  try {
    const AudioContextClass =
      window.AudioContext || window.webkitAudioContext;

    if (!AudioContextClass) return;

    const audioContext = new AudioContextClass();
    const now = audioContext.currentTime;

    const masterGain = audioContext.createGain();
    masterGain.gain.setValueAtTime(0.0001, now);
    masterGain.gain.exponentialRampToValueAtTime(0.32, now + 0.008);
    masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.72);
    masterGain.connect(audioContext.destination);

    const createMetalTone = ({
      frequency,
      startOffset = 0,
      duration = 0.5,
      volume = 0.18,
      type = "sine",
    }) => {
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();

      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, now + startOffset);
      oscillator.frequency.exponentialRampToValueAtTime(
        Math.max(120, frequency * 0.72),
        now + startOffset + duration
      );

      gain.gain.setValueAtTime(0.0001, now + startOffset);
      gain.gain.exponentialRampToValueAtTime(
        Math.max(0.001, volume),
        now + startOffset + 0.006
      );
      gain.gain.exponentialRampToValueAtTime(
        0.0001,
        now + startOffset + duration
      );

      oscillator.connect(gain);
      gain.connect(masterGain);

      oscillator.start(now + startOffset);
      oscillator.stop(now + startOffset + duration + 0.03);
    };

    // Metallic "khan" / coin-drop style layers.
    createMetalTone({
      frequency: 1480,
      startOffset: 0,
      duration: 0.34,
      volume: 0.22,
      type: "triangle",
    });

    createMetalTone({
      frequency: 2180,
      startOffset: 0.012,
      duration: 0.43,
      volume: 0.14,
      type: "sine",
    });

    createMetalTone({
      frequency: 860,
      startOffset: 0.025,
      duration: 0.58,
      volume: 0.12,
      type: "triangle",
    });

    window.setTimeout(() => {
      try {
        if (audioContext.state !== "closed") {
          audioContext.close();
        }
      } catch {
        // Ignore browser-specific close errors.
      }
    }, 1000);
  } catch (error) {
    console.error("Coin reward sound error:", error);
  }
};

function FoodRadarOrderForm() {
  const navigate = useNavigate();
  const { vendorId } = useParams();
  const [searchParams] = useSearchParams();

  const fromNotification =
    String(
      searchParams.get('fromNotification') ||
      ''
    ) === '1';

  const notificationId =
    Number(
      searchParams.get('notificationId') ||
      searchParams.get('notification_id') ||
      0
    ) || 0;

  const notificationKey =
    String(
      searchParams.get('notificationKey') ||
      searchParams.get('notification_key') ||
      searchParams.get('key') ||
      ''
    ).trim();

  const [step, setStep] = useState(STEP_MODE);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [vendorProfile, setVendorProfile] = useState({});
  const [orderSettings, setOrderSettings] = useState(normalizeOrderSettings({}));
  const [products, setProducts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [selectedBillOfferId, setSelectedBillOfferId] = useState(null);

  const [customer, setCustomer] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [showLoginCard, setShowLoginCard] = useState(false);
  const [loginSaving, setLoginSaving] = useState(false);
  const [loginForm, setLoginForm] = useState({
    name: "",
    mobile: "",
    email: "",
  });

  const [orderMode, setOrderMode] = useState("dine_in");
  const [paymentMode, setPaymentMode] = useState("cash");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [showCategorySidebar, setShowCategorySidebar] = useState(false);
  const [cart, setCart] = useState([]);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);

  // Vendor-configured coin redemption
  const [availableCoins, setAvailableCoins] = useState(0);
  const [selectedCoinsToUse, setSelectedCoinsToUse] = useState(0);
  const [coinWalletLoading, setCoinWalletLoading] = useState(false);

  // Animated coin reward popup
  const [showCoinRewardPopup, setShowCoinRewardPopup] = useState(false);
  const [coinRewardPopupValue, setCoinRewardPopupValue] = useState(0);

  // Food Radar rating popup
  const [showRatingPopup, setShowRatingPopup] = useState(false);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingHoverValue, setRatingHoverValue] = useState(0);
  const [ratingReviewText, setRatingReviewText] = useState("");
  const [ratingSaving, setRatingSaving] = useState(false);
  const [ratingMessage, setRatingMessage] = useState("");

  const [addressForm, setAddressForm] = useState({
    label: "Profile Address",
    address_line1: "",
    address_line2: "",
    landmark: "",
    city: "",
    state: "",
    pincode: "",
    delivery_notes: "",
    latitude: "",
    longitude: "",
  });

  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState("profile");
  const [deliveryCheck, setDeliveryCheck] = useState({
    checked: false,
    allowed: true,
    message: "",
    distanceKm: null,
    radiusKm: null,
  });

  const shopName =
    vendorProfile.shop_name ||
    vendorProfile.shopName ||
    vendorProfile.name ||
    "Food Shop";

  const shopImage =
    vendorProfile.shop_logo ||
    vendorProfile.shopLogo ||
    vendorProfile.shop_image ||
    vendorProfile.shopImage ||
    "";

  const dineInEnabled = safeFlag(orderSettings.enable_dine_in, 1) === 1;
  const pickupEnabled = safeFlag(orderSettings.enable_pickup, 1) === 1;
  const deliveryEnabled = safeFlag(orderSettings.enable_home_delivery, 0) === 1;
  const codAllowed = safeFlag(orderSettings.allow_cod, 1) === 1;
  const onlineAllowed = safeFlag(orderSettings.allow_online_payment, 1) === 1;

  const categories = useMemo(() => {
    const fromProducts = products.map((p) => p.category || "Others").filter(Boolean);
    return [...new Set([...CATEGORY_OPTIONS, ...fromProducts])];
  }, [products]);

  const categoryCounts = useMemo(() => {
    const counts = { All: products.length };

    products.forEach((product) => {
      const key = product.category || "Others";
      counts[key] = (counts[key] || 0) + 1;
    });

    return counts;
  }, [products]);

  const activeCartItems = useMemo(
    () => cart.filter((item) => Number(item.quantity || 0) > 0),
    [cart]
  );

  const cartCount = useMemo(
    () => cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
    [cart]
  );

  const baseTotal = useMemo(
    () =>
      activeCartItems.reduce(
        (sum, item) => sum + Number(item.selling_price || item.price || 0) * Number(item.quantity || 0),
        0
      ),
    [activeCartItems]
  );

  const normalizedOffers = useMemo(() => {
    return safeArray(offers)
      .map((offer) => {
        const offerJson =
          parseJsonMaybe(
            offer.offer_json || offer.metadata,
            {}
          ) || {};

        return {
          ...offerJson,
          ...offer,
          id: Number(offer.id || offer.offer_id || 0),
          offer_type: String(
            offer.offer_type ||
              offer.offerType ||
              offerJson.offer_type ||
              ""
          )
            .trim()
            .toLowerCase(),
          combo_items: safeArray(
            offer.combo_items || offerJson.combo_items
          ),
          progressive_steps: safeArray(
            offer.progressive_steps ||
              offerJson.progressive_steps
          ),
        };
      })
      .filter(
        (offer) =>
          Number(offer.id || 0) > 0 &&
          safeFlag(offer.is_active ?? 1, 1) === 1 &&
          true
      );
  }, [offers]);

  const offersByType = useMemo(() => {
    const groups = {
      bill_discount: [],
      combo: [],
      bill_unlock_addon: [],
      buy_x_get_y: [],
      time_slot: [],
      progressive_unlock: [],
      coin_reward: [],
      other: [],
    };

    normalizedOffers.forEach((offer) => {
      const type = offer.offer_type;
      if (groups[type]) groups[type].push(offer);
      else groups.other.push(offer);
    });

    return groups;
  }, [normalizedOffers]);

  const billDiscountOffers = offersByType.bill_discount;
  const comboOffers = offersByType.combo;
  const addonOffers = offersByType.bill_unlock_addon;
  const buyXGetYOffers = offersByType.buy_x_get_y;
  const timeSlotOffers = offersByType.time_slot;
  const progressiveOffers = offersByType.progressive_unlock;
  const coinRewardOffers = offersByType.coin_reward;

  const activeCoinRewardOffer = useMemo(
    () => coinRewardOffers[0] || null,
    [coinRewardOffers]
  );

  const coinRedeemConfig = useMemo(() => {
    const offer = activeCoinRewardOffer || {};
    const json = parseJsonMaybe(offer.offer_json, {}) || {};

    return {
      coinValueRupees: Math.max(
        0,
        safeNumber(json.coin_value_rupees ?? offer.coin_value_rupees, 0)
      ),
      minRedeem: Math.max(
        0,
        Math.floor(safeNumber(json.coin_min_redeem ?? offer.coin_min_redeem, 0))
      ),
      maxRedeemPerOrder: Math.max(
        0,
        Math.floor(
          safeNumber(
            json.coin_max_redeem_per_order ?? offer.coin_max_redeem_per_order,
            0
          )
        )
      ),
      minOrderForRedeem: Math.max(
        0,
        safeNumber(
          json.coin_min_order_for_redeem ?? offer.coin_min_order_for_redeem,
          0
        )
      ),
    };
  }, [activeCoinRewardOffer]);

  const isTimeSlotActiveClient = (offer) => {
    const now = new Date();
    const indiaParts = new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Kolkata",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(now);

    const map = {};
    indiaParts.forEach((part) => {
      map[part.type] = part.value;
    });

    const dayMap = {
      Sun: "sun",
      Mon: "mon",
      Tue: "tue",
      Wed: "wed",
      Thu: "thu",
      Fri: "fri",
      Sat: "sat",
    };

    const currentDay = dayMap[map.weekday] || "";
    const currentMinutes =
      Number(map.hour || 0) * 60 +
      Number(map.minute || 0);

    const parseMinutes = (value) => {
      const match = String(value || "").match(
        /^(\d{1,2}):(\d{2})/
      );
      if (!match) return null;
      return Number(match[1]) * 60 + Number(match[2]);
    };

    const start = parseMinutes(offer.start_time);
    const end = parseMinutes(offer.end_time);
    const days = safeArray(offer.days_active).map((day) =>
      String(day || "").slice(0, 3).toLowerCase()
    );

    if (start === null || end === null) return false;
    if (days.length && !days.includes(currentDay)) return false;

    return start <= end
      ? currentMinutes >= start && currentMinutes <= end
      : currentMinutes >= start || currentMinutes <= end;
  };

  const getProgressiveUnlockedStep = (offer) =>
    safeArray(offer.progressive_steps)
      .filter(
        (step) =>
          baseTotal >=
          Number(
            step.min_bill_amount ||
              step.minBillAmount ||
              step.amount ||
              0
          )
      )
      .sort(
        (a, b) =>
          Number(
            b.min_bill_amount ||
              b.minBillAmount ||
              b.amount ||
              0
          ) -
          Number(
            a.min_bill_amount ||
              a.minBillAmount ||
              a.amount ||
              0
          )
      )[0] || null;

  const discountLikeOffers = useMemo(() => {
    const standard = billDiscountOffers.map((offer) => ({
      ...offer,
      eligible:
        baseTotal >=
          Number(
            offer.min_bill_amount ||
              offer.min_purchasing_price ||
              0
          ) &&
        (Number(offer.max_purchasing_price || 0) <= 0 ||
          baseTotal <=
            Number(offer.max_purchasing_price || 0)),
    }));

    const timed = timeSlotOffers.map((offer) => ({
      ...offer,
      eligible:
        isTimeSlotActiveClient(offer) &&
        baseTotal >=
          Number(
            offer.min_bill_amount ||
              offer.min_purchasing_price ||
              0
          ),
    }));

    const progressive = progressiveOffers.map((offer) => ({
      ...offer,
      unlocked_step: getProgressiveUnlockedStep(offer),
      eligible: Boolean(getProgressiveUnlockedStep(offer)),
    }));

    return [...standard, ...timed, ...progressive];
  }, [
    billDiscountOffers,
    timeSlotOffers,
    progressiveOffers,
    baseTotal,
  ]);

  useEffect(() => {
    if (
      selectedBillOfferId &&
      !discountLikeOffers.some(
        (offer) =>
          Number(offer.id) ===
            Number(selectedBillOfferId) &&
          offer.eligible
      )
    ) {
      setSelectedBillOfferId(null);
    }
  }, [discountLikeOffers, selectedBillOfferId]);

  const selectedBillDiscount = useMemo(
    () =>
      discountLikeOffers.find(
        (offer) =>
          Number(offer.id) ===
          Number(selectedBillOfferId)
      ) || null,
    [discountLikeOffers, selectedBillOfferId]
  );

  const getOfferDiscountPreview = (offer) => {
    if (!offer) return 0;

    if (offer.offer_type === "progressive_unlock") {
      const step = offer.unlocked_step;
      if (!step) return 0;

      const rewardType = String(
        step.reward_type ||
          step.rewardType ||
          step.type ||
          ""
      ).toLowerCase();

      if (
        rewardType === "percent" ||
        rewardType === "percentage" ||
        rewardType === "percent_discount"
      ) {
        return (
          (baseTotal *
            Number(
              step.reward_value ||
                step.rewardValue ||
                step.value ||
                0
            )) /
          100
        );
      }

      if (
        !(
          step.reward_product_id ||
          step.rewardProductId ||
          step.productId
        )
      ) {
        return Number(
          step.reward_value ||
            step.rewardValue ||
            step.value ||
            0
        );
      }

      return 0;
    }

    const raw =
      String(offer.discount_type || "flat").toLowerCase() ===
      "percent"
        ? (baseTotal *
            Number(offer.discount_value || 0)) /
          100
        : Number(offer.discount_value || 0);

    const cap = Number(offer.max_discount || 0);
    return Math.max(
      0,
      Math.min(cap > 0 ? Math.min(raw, cap) : raw, baseTotal)
    );
  };

  const discountAmount = Number(
    getOfferDiscountPreview(selectedBillDiscount) || 0
  );

  const deliveryFee =
    orderMode === "delivery"
      ? Number(orderSettings.delivery_fee || 0)
      : 0;

  const packagingFee =
    orderMode === "takeaway"
      ? Number(orderSettings.packaging_fee || 0)
      : 0;

  const extraCharges = deliveryFee + packagingFee;
  const amountBeforeCoins = Math.max(
    0,
    baseTotal + extraCharges - discountAmount
  );

  const maxCoinsByBill =
    coinRedeemConfig.coinValueRupees > 0
      ? Math.floor(amountBeforeCoins / coinRedeemConfig.coinValueRupees)
      : 0;

  const maximumUsableCoins = Math.max(
    0,
    Math.min(
      availableCoins,
      coinRedeemConfig.maxRedeemPerOrder > 0
        ? coinRedeemConfig.maxRedeemPerOrder
        : availableCoins,
      maxCoinsByBill
    )
  );

  const coinRedeemEligible =
    Boolean(activeCoinRewardOffer) &&
    coinRedeemConfig.coinValueRupees > 0 &&
    availableCoins >= coinRedeemConfig.minRedeem &&
    amountBeforeCoins >= coinRedeemConfig.minOrderForRedeem &&
    maximumUsableCoins >= coinRedeemConfig.minRedeem;

  const appliedCoins = coinRedeemEligible
    ? Math.min(Math.max(0, Number(selectedCoinsToUse || 0)), maximumUsableCoins)
    : 0;

  const coinDiscountAmount = Math.min(
    amountBeforeCoins,
    appliedCoins * coinRedeemConfig.coinValueRupees
  );

  useEffect(() => {
    if (!coinRedeemEligible) {
      setSelectedCoinsToUse(0);
      return;
    }

    setSelectedCoinsToUse((current) =>
      Math.min(Math.max(0, Number(current || 0)), maximumUsableCoins)
    );
  }, [coinRedeemEligible, maximumUsableCoins]);

  const finalTotal = Math.max(
    0,
    amountBeforeCoins - coinDiscountAmount
  );

  const currentMinOrder = useMemo(() => {
    if (orderMode !== "delivery") return 0;
    return Math.max(
      0,
      Number(orderSettings.min_order_amount || 0),
      Number(orderSettings.delivery_min_amount || 0)
    );
  }, [orderMode, orderSettings]);

  const filteredProducts = useMemo(() => {
    const value = search.trim().toLowerCase();

    return products.filter((product) => {
      const text = `${product.name || ""} ${product.category || ""} ${product.description || ""}`.toLowerCase();
      const availability = String(product.availability || "available").toLowerCase();
      const activeOk = Number(product.is_active ?? 1) === 1;
      const unavailableStatus = ["out_of_stock", "out-of-stock", "unavailable", "off", "inactive"].includes(availability);
      const stockOk =
        !unavailableStatus &&
        (!product.has_stock_quantity ||
          Number(product.stock_quantity ?? 0) > 0 ||
          ["in_stock", "instock", "available", "active"].includes(availability));
      const searchOk = !value || text.includes(value);
      const categoryOk = categoryFilter === "All" || product.category === categoryFilter;
      return activeOk && stockOk && searchOk && categoryOk;
    });
  }, [products, search, categoryFilter]);

  const groupedProducts = useMemo(() => {
    const map = {};
    filteredProducts.forEach((product) => {
      const key = product.category || "Others";
      if (!map[key]) map[key] = [];
      map[key].push(product);
    });
    return map;
  }, [filteredProducts]);

  useEffect(() => {
    loadInitialData();
  }, [vendorId]);

  useEffect(() => {
    const enabledModes = [
      dineInEnabled ? "dine_in" : null,
      pickupEnabled ? "takeaway" : null,
      deliveryEnabled ? "delivery" : null,
    ].filter(Boolean);

    if (!enabledModes.includes(orderMode)) {
      setOrderMode(enabledModes[0] || "takeaway");
    }
  }, [dineInEnabled, pickupEnabled, deliveryEnabled, orderMode]);

  const clearMessages = () => {
    setMessage("");
    setErrorMessage("");
  };

  const trackNotificationConversion = async ({
    eventType,
    orderId = null,
    orderAmount = null,
  }) => {
    if (
      !fromNotification ||
      !notificationId ||
      !notificationKey ||
      !eventType
    ) {
      return;
    }

    try {
      await fetch(
        `${window.location.origin}/api/control-center/notifications/public/conversion-event`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            queueId: notificationId,
            landingKey: notificationKey,
            eventType,
            vendorId: Number(vendorId || 0) || null,
            orderId,
            orderAmount,
          }),
          keepalive: true,
        }
      );
    } catch (trackError) {
      console.warn(
        "Notification conversion tracking warning:",
        eventType,
        trackError
      );
    }
  };

  const fetchVendorProfile = async () => {
    let primaryProfile = {};
    let fallbackProfile = {};

    try {
      const response = await axios.get(`${apiUrl}/public/${businessType}/profile`, {
        params: { vendorId },
      });
      primaryProfile = normalizeVendorProfilePayload(response.data);
    } catch (primaryError) {
      primaryProfile = {};
    }

    try {
      const fallback = await axios.get(`${apiUrl}/public/order/${businessType}/profile`, {
        params: { vendorId },
      });
      fallbackProfile = normalizeVendorProfilePayload(fallback.data);
    } catch (fallbackError) {
      fallbackProfile = {};
    }

    return {
      ...fallbackProfile,
      ...primaryProfile,
      shop_latitude: primaryProfile.shop_latitude ?? fallbackProfile.shop_latitude ?? null,
      shop_longitude: primaryProfile.shop_longitude ?? fallbackProfile.shop_longitude ?? null,
      shopLatitude: primaryProfile.shop_latitude ?? fallbackProfile.shop_latitude ?? null,
      shopLongitude: primaryProfile.shop_longitude ?? fallbackProfile.shop_longitude ?? null,
    };
  };

  const checkDeliveryAvailability = (addr = addressForm, profileOverride = vendorProfile, settingsOverride = orderSettings) => {
    const radiusKm = Number(settingsOverride.delivery_radius_km || 0);
    const shopLat = parseCoordinate(
      profileOverride.shop_latitude ?? profileOverride.shopLatitude ?? profileOverride.latitude ?? profileOverride.lat
    );
    const shopLng = parseCoordinate(
      profileOverride.shop_longitude ?? profileOverride.shopLongitude ?? profileOverride.longitude ?? profileOverride.lng
    );
    const customerLat = parseCoordinate(addr.latitude);
    const customerLng = parseCoordinate(addr.longitude);

    if (orderMode !== "delivery") {
      const result = { checked: false, allowed: true, message: "", distanceKm: null, radiusKm };
      setDeliveryCheck(result);
      return result;
    }

    if (!radiusKm || radiusKm <= 0) {
      const result = {
        checked: false,
        allowed: true,
        message: "Delivery radius set nahi hai. Order place karte waqt backend final check karega.",
        distanceKm: null,
        radiusKm: 0,
      };
      setDeliveryCheck(result);
      return result;
    }

    if (!isUsableCoordinatePair(shopLat, shopLng)) {
      const result = {
        checked: true,
        allowed: false,
        message: "Shop location set nahi hai. Vendor settings/signup me shop location update karna padega.",
        distanceKm: null,
        radiusKm,
      };
      setDeliveryCheck(result);
      return result;
    }

    if (!isUsableCoordinatePair(customerLat, customerLng)) {
      const result = {
        checked: true,
        allowed: false,
        message: "Is address me location pin nahi hai. Work Address select karke current location allow karein.",
        distanceKm: null,
        radiusKm,
      };
      setDeliveryCheck(result);
      return result;
    }

    const distanceKm = calculateDistanceKm(shopLat, shopLng, customerLat, customerLng);
    const roundedDistance = distanceKm === null ? null : Math.round(distanceKm * 100) / 100;
    const allowed = roundedDistance !== null && roundedDistance <= radiusKm;

    const result = {
      checked: true,
      allowed,
      message: allowed
        ? `Delivery available hai. Distance ${roundedDistance} KM hai, radius ${radiusKm} KM.`
        : `Delivery available nahi hai. Distance ${roundedDistance} KM hai, shop sirf ${radiusKm} KM radius tak delivery karta hai.`,
      distanceKm: roundedDistance,
      radiusKm,
    };

    setDeliveryCheck(result);
    return result;
  };

  const applyAddress = (addr = {}, option = "profile", profileOverride = vendorProfile, settingsOverride = orderSettings) => {
    const normalized = normalizeCustomerAddress(addr, option === "work" ? "Work Address" : "Profile Address");
    const next = {
      label: normalized.label,
      address_line1: normalized.address_line1,
      address_line2: normalized.address_line2,
      landmark: normalized.landmark,
      city: normalized.city,
      state: normalized.state,
      pincode: normalized.pincode,
      delivery_notes: normalized.delivery_notes,
      latitude: normalized.latitude ?? "",
      longitude: normalized.longitude ?? "",
    };

    setSelectedAddressId(String(normalized.id || option));
    setSelectedDeliveryOption(option);
    setAddressForm(next);
    checkDeliveryAvailability(next, profileOverride, settingsOverride);
    return next;
  };

  const selectProfileAddress = (addressesOverride = customerAddresses, customerOverride = customer, profileOverride = vendorProfile, settingsOverride = orderSettings) => {
    const profileAddress = getCustomerProfileAddress(customerOverride || {}, addressesOverride || []);

    if (!profileAddress) {
      setSelectedDeliveryOption("profile");
      setSelectedAddressId("profile-missing");
      setDeliveryCheck({
        checked: true,
        allowed: false,
        message: "Profile address save nahi hai. Pehle customer profile/address save karein.",
        distanceKm: null,
        radiusKm: Number(settingsOverride.delivery_radius_km || 0),
      });
      return null;
    }

    return applyAddress(profileAddress, "profile", profileOverride, settingsOverride);
  };

  const selectWorkAddress = (addressesOverride = customerAddresses, profileOverride = vendorProfile, settingsOverride = orderSettings) => {
    const savedWorkAddress = getWorkAddress(addressesOverride || []);

    if (!navigator.geolocation) {
      setErrorMessage("Location support nahi mil raha hai. Work Address ke liye current location required hai.");
      return;
    }

    setSelectedDeliveryOption("work");
    setSelectedAddressId("work-current-detecting");
    setAddressForm({
      label: "Work Address",
      address_line1: "Current address detect ho raha hai...",
      address_line2: "",
      landmark: "",
      city: "",
      state: "",
      pincode: "",
      delivery_notes: "",
      latitude: "",
      longitude: "",
    });
    setDeliveryCheck({
      checked: false,
      allowed: false,
      message: "Work Address ke liye current location detect ho rahi hai...",
      distanceKm: null,
      radiusKm: Number(settingsOverride.delivery_radius_km || 0),
    });
    setMessage("Work Address ke liye current location detect ho rahi hai...");
    setErrorMessage("");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const latitude = Number(position.coords.latitude).toFixed(8);
        const longitude = Number(position.coords.longitude).toFixed(8);
        const currentAddress = await reverseGeocodeCurrentAddress(latitude, longitude);

        const currentWorkAddress = normalizeCustomerAddress(
          {
            ...(savedWorkAddress || {}),
            label: "Work Address",
            address_line1:
              currentAddress?.address_line1 ||
              savedWorkAddress?.address_line1 ||
              savedWorkAddress?.address ||
              `Current Location (${Number(latitude).toFixed(5)}, ${Number(longitude).toFixed(5)})`,
            address_line2: currentAddress?.address_line2 || savedWorkAddress?.address_line2 || "",
            city: currentAddress?.city || savedWorkAddress?.city || "",
            state: currentAddress?.state || savedWorkAddress?.state || "",
            pincode: currentAddress?.pincode || savedWorkAddress?.pincode || "",
            latitude,
            longitude,
          },
          "Work Address"
        );

        applyAddress(currentWorkAddress, "work", profileOverride, settingsOverride);
        setMessage("Work current address detect ho gaya. Delivery availability check ho gaya.");
      },
      (error) => {
        console.error("Work location error:", error);
        setErrorMessage("Work Address ke liye location allow nahi hua. Browser/app settings me location allow karein.");
        setDeliveryCheck({
          checked: true,
          allowed: false,
          message: "Work Address ke liye current location allow karna zaruri hai.",
          distanceKm: null,
          radiusKm: Number(settingsOverride.delivery_radius_km || 0),
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  const fetchFoodRadarCustomer = async (profileOverride = vendorProfile, settingsOverride = orderSettings) => {
    const savedCustomerId = localStorage.getItem("foodRadarCustomerId") || "";
    const savedMobile = localStorage.getItem("foodRadarCustomerMobile") || "";

    if (!savedCustomerId && !savedMobile) {
      setShowLoginCard(true);
      return;
    }

    try {
      const response = await axios.get(
        `${apiUrl}/public/food-radar/customers/${encodeURIComponent(savedCustomerId)}`,
        {
          params: {
            mobile: savedMobile,
          },
        }
      );

      const data = response.data || {};
      const nextCustomer = data.customer || data.data || data.profile || null;
      const addresses = safeArray(data.addresses || data.customer_addresses || data.addressList);

      if (!nextCustomer) {
        setLoginForm((prev) => ({ ...prev, mobile: savedMobile }));
        setShowLoginCard(true);
        return;
      }

      const resolvedCustomerId = getFoodRadarCustomerId(nextCustomer);

      if (resolvedCustomerId) {
        localStorage.setItem(
          "foodRadarCustomerId",
          String(resolvedCustomerId)
        );
      }

      setCustomer({
        ...nextCustomer,
        id: resolvedCustomerId || nextCustomer.id || null,
      });

      await fetchVendorCoinWallet(
        resolvedCustomerId || nextCustomer.id || null
      );
      setCustomerAddresses(addresses);
      setLoginForm({
        name: nextCustomer.name || nextCustomer.customer_name || "",
        mobile: normalizeMobile(nextCustomer.mobile || nextCustomer.customer_mobile || savedMobile),
        email: nextCustomer.email || "",
      });

      selectProfileAddress(addresses, nextCustomer, profileOverride, settingsOverride);
    } catch (error) {
      console.error("Food Radar customer fetch error:", error);
      setLoginForm((prev) => ({ ...prev, mobile: savedMobile }));
      setShowLoginCard(true);
    }
  };

  const fetchVendorCoinWallet = async (customerIdOverride = null) => {
    const customerId =
      Number(customerIdOverride || getFoodRadarCustomerId(customer) || 0);

    if (!customerId || !vendorId) {
      setAvailableCoins(0);
      setSelectedCoinsToUse(0);
      return;
    }

    try {
      setCoinWalletLoading(true);

      const customerMobile =
        localStorage.getItem("foodRadarCustomerMobile") ||
        normalizeMobile(
          loginForm.mobile ||
            customer?.mobile ||
            customer?.customer_mobile ||
            ""
        );

      const response = await axios.get(
        `${apiUrl}/public/food-radar/rewards`,
        {
          params: {
            customerId,
            customer_id: customerId,
            mobile: customerMobile || undefined,
          },
        }
      );

      const source =
        response.data?.vendors ||
        response.data?.rewards ||
        response.data?.wallets ||
        response.data?.data ||
        [];

      const vendors = Array.isArray(source) ? source : [];

      const currentVendor = vendors.find((item) => {
        const itemVendorId = Number(
          item.vendorId ||
            item.vendor_id ||
            item.id ||
            0
        );

        return itemVendorId === Number(vendorId);
      });

      let balance = Math.max(
        0,
        Math.floor(
          safeNumber(
            currentVendor?.available_coins ??
              currentVendor?.availableCoins ??
              currentVendor?.customer_available_coins ??
              currentVendor?.coins_available ??
              currentVendor?.balance ??
              0,
            0
          )
        )
      );

      /*
        Rewards page Use Coins button vendor balance sessionStorage me
        save karta hai. API response me temporary issue ho to same verified
        vendor ka cached balance display fallback ke roop me use karenge.
        Backend order placement ke time DB wallet ko FOR UPDATE ke saath
        dobara validate karega, isliye security client balance par depend
        nahi karti.
      */
      if (balance <= 0) {
        const cachedVendorId = Number(
          sessionStorage.getItem("foodRadarRedeemVendor") || 0
        );

        const cachedCoins = Math.max(
          0,
          Math.floor(
            safeNumber(
              sessionStorage.getItem(
                "foodRadarRedeemAvailableCoins"
              ),
              0
            )
          )
        );

        if (
          cachedVendorId === Number(vendorId) &&
          cachedCoins > 0
        ) {
          balance = cachedCoins;
        }
      }

      setAvailableCoins(balance);

      setSelectedCoinsToUse((current) =>
        Math.min(Math.max(0, Number(current || 0)), balance)
      );
    } catch (error) {
      console.error("Coin wallet fetch error:", error);

      const cachedVendorId = Number(
        sessionStorage.getItem("foodRadarRedeemVendor") || 0
      );

      const cachedCoins = Math.max(
        0,
        Math.floor(
          safeNumber(
            sessionStorage.getItem(
              "foodRadarRedeemAvailableCoins"
            ),
            0
          )
        )
      );

      if (
        cachedVendorId === Number(vendorId) &&
        cachedCoins > 0
      ) {
        setAvailableCoins(cachedCoins);

        setSelectedCoinsToUse((current) =>
          Math.min(Math.max(0, Number(current || 0)), cachedCoins)
        );
      } else {
        setAvailableCoins(0);
        setSelectedCoinsToUse(0);
      }
    } finally {
      setCoinWalletLoading(false);
    }
  };

  const loadInitialData = async () => {
    if (!vendorId) {
      setErrorMessage("Invalid vendor. Food Radar order page open nahi ho sakta.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      clearMessages();

      const [profileRes, productsRes, settingsRes, offersRes] = await Promise.all([
        fetchVendorProfile(),
        axios.get(`${apiUrl}/public/products`, {
          params: { vendorId, businessType },
        }),
        axios.get(`${apiUrl}/public/order-settings`, {
          params: {
            vendorId,
            vendorType: businessType,
            userdb: "true",
          },
        }),
        (async () => {
          try {
            // Order page must use the same checkout-offers API that already
            // works in the QR ordering flow.
            const checkoutOffersResponse = await axios.get(
              `${apiUrl}/public/offers`,
              {
                params: {
                  vendorId,
                  vendorType: businessType,
                  businessType,
                  userdb: "true",
                },
              }
            );

            console.log(
              "FOOD RADAR CHECKOUT OFFERS RESPONSE:",
              checkoutOffersResponse.data
            );

            return checkoutOffersResponse;
          } catch (checkoutOfferError) {
            console.error(
              "Checkout offers API failed, trying Food Radar discovery API:",
              checkoutOfferError
            );

            try {
              const discoveryOffersResponse = await axios.get(
                `${apiUrl}/public/food-radar/offers`,
                {
                  params: {
                    vendorId,
                    vendorType: businessType,
                  },
                }
              );

              console.log(
                "FOOD RADAR DISCOVERY OFFERS RESPONSE:",
                discoveryOffersResponse.data
              );

              return discoveryOffersResponse;
            } catch (discoveryOfferError) {
              console.error(
                "Both offer APIs failed:",
                discoveryOfferError
              );

              return {
                data: {
                  success: false,
                  offers: [],
                  vendors: [],
                },
              };
            }
          }
        })(),
      ]);

      const rawProducts = extractProductsFromApi(productsRes.data);
      const rawOffers = extractOffersFromApi(offersRes.data);
      console.log("FOOD RADAR RAW OFFERS:", rawOffers);

      const normalizedProductList = rawProducts
        .map(normalizeProduct)
        .filter((product) => Number(product.id || product.productId || 0) > 0);

      console.log("FOOD RADAR ORDER PRODUCTS RESPONSE:", productsRes.data);
      console.log("FOOD RADAR ORDER PRODUCTS NORMALIZED:", normalizedProductList);

      const normalizedSettings = normalizeOrderSettings(settingsRes.data || {});
      const normalizedProfile = normalizeVendorProfilePayload(profileRes || {});

      setVendorProfile(normalizedProfile);
      setProducts(normalizedProductList);
      setOrderSettings(normalizedSettings);
      setOffers(rawOffers);

      if (!rawOffers.length) {
        console.warn(
          "No active non-coin offers returned for vendor:",
          vendorId
        );
      }

      await fetchFoodRadarCustomer(normalizedProfile, normalizedSettings);
    } catch (error) {
      console.error("Food Radar order load error:", error);
      setErrorMessage(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Order page load nahi hua."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCustomer = async (e) => {
    e.preventDefault();

    const mobile = normalizeMobile(loginForm.mobile);
    if (mobile.length !== 10) {
      setErrorMessage("Valid 10 digit mobile number required hai.");
      return;
    }

    try {
      setLoginSaving(true);
      clearMessages();

      const response = await fetch("/api/public/food-radar/customers/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: loginForm.name,
          mobile,
          email: loginForm.email,
          default_address: "",
          area: "Food Radar Order",
          city: "Mumbai",
        }),
      });

      const data = await response.json();

      if (!data.success || !data.customer) {
        throw new Error(data.message || "Customer save nahi hua.");
      }

      const resolvedCustomerId = getFoodRadarCustomerId(
        data.customer
      );

      if (!resolvedCustomerId) {
        throw new Error(
          "Customer profile ID response me missing hai."
        );
      }

      localStorage.setItem(
        "foodRadarCustomerId",
        String(resolvedCustomerId)
      );
      localStorage.setItem(
        "foodRadarCustomerMobile",
        data.customer.mobile || mobile
      );
      setCustomer({
        ...data.customer,
        id: resolvedCustomerId,
      });
      await fetchVendorCoinWallet(resolvedCustomerId);
      setShowLoginCard(false);
      selectProfileAddress([], data.customer);
      setMessage("Profile save ho gaya. Profile Address auto fill ho gaya.");
    } catch (error) {
      setErrorMessage(error.message || "Customer profile save nahi hua.");
    } finally {
      setLoginSaving(false);
    }
  };

  const getCartItem = (productId) =>
    cart.find((item) => Number(item.productId || item.id) === Number(productId)) || null;

  const upsertCartItem = (product, nextQty) => {
    const qty = Math.max(0, Number(nextQty || 0));

    setCart((prev) => {
      if (qty <= 0) {
        return prev.filter((item) => Number(item.productId || item.id) !== Number(product.id));
      }

      const payload = {
        productId: Number(product.id),
        id: Number(product.id),
        name: product.name,
        image: product.image_url || product.image || "",
        category: product.category || "",
        food_type: product.food_type || "veg",
        description: product.description || "",
        selling_price: Number(product.selling_price || product.price || 0),
        price: Number(product.price || product.selling_price || 0),
        stock_quantity: Number(product.stock_quantity || 999999),
        quantity: qty,
      };

      const exists = prev.some((item) => Number(item.productId || item.id) === Number(product.id));
      return exists
        ? prev.map((item) =>
            Number(item.productId || item.id) === Number(product.id) ? payload : item
          )
        : [...prev, payload];
    });
  };

  const addProductToCart = (product) => {
    const existing = getCartItem(product.id);
    const currentQty = Number(existing?.quantity || 0);
    const nextQty = currentQty + 1;
    const stockQty = Number(product.stock_quantity || 999999);

    if (stockQty > 0 && nextQty > stockQty) {
      setMessage(`"${product.name}" ka available stock sirf ${stockQty} hai.`);
      return;
    }

    upsertCartItem(product, nextQty);
  };

  const decreaseQty = (product) => {
    const existing = getCartItem(product.id || product.productId);
    if (!existing) return;
    upsertCartItem(product, Number(existing.quantity || 0) - 1);
  };

  const addComboOfferToCart = (offer) => {
    const offerId = Number(offer.id || 0);
    if (!offerId) return;

    const cartKey = `combo_${offerId}`;

    setCart((prev) => {
      if (
        prev.some(
          (item) => String(item.cartKey || item.id) === cartKey
        )
      ) {
        return prev;
      }

      return [
        ...prev,
        {
          cartKey,
          type: "combo",
          offerType: "combo",
          offerId,
          productId: cartKey,
          id: cartKey,
          name:
            offer.offer_name ||
            offer.combo_name ||
            offer.offer_text ||
            "Combo Offer",
          image: offer.image || "",
          description: safeArray(offer.combo_items)
            .map(
              (item) =>
                `${item.name || "Item"} x${Number(
                  item.quantity || item.qty || 1
                )}`
            )
            .join(", "),
          selling_price: Number(offer.combo_price || 0),
          price: Number(offer.combo_price || 0),
          quantity: 1,
          isOfferItem: true,
        },
      ];
    });

    setMessage("Combo cart me add ho gaya.");
  };

  const addAddonOfferToCart = (offer) => {
    const offerId = Number(offer.id || 0);
    if (!offerId) return;

    const minBill = Number(
      offer.min_bill_amount || offer.min_purchasing_price || 0
    );

    if (baseTotal < minBill) {
      setErrorMessage(
        `Ye add-on unlock karne ke liye ${formatMoney(
          minBill - baseTotal
        )} aur add karein.`
      );
      return;
    }

    const cartKey = `addon_${offerId}`;

    setCart((prev) => {
      if (
        prev.some(
          (item) => String(item.cartKey || item.id) === cartKey
        )
      ) {
        return prev;
      }

      return [
        ...prev,
        {
          cartKey,
          type: "addon_offer",
          offerType: "bill_unlock_addon",
          offerId,
          productId: cartKey,
          id: cartKey,
          name:
            offer.addon_product_name ||
            offer.offer_name ||
            "Add-on Deal",
          offer_name: offer.offer_name || null,
          image: offer.image || "",
          description: `Unlocked on bill ${formatMoney(minBill)}+`,
          selling_price: Number(offer.addon_special_price || 0),
          price: Number(offer.addon_special_price || 0),
          original_price: Number(offer.addon_original_price || 0),
          quantity: 1,
          isOfferItem: true,
        },
      ];
    });

    setMessage("Add-on cart me add ho gaya.");
  };

  const addBuyXGetYOfferToCart = (offer) => {
    const offerId = Number(offer.id || 0);
    const buyProductId = Number(offer.buy_product_id || 0);
    const requiredQty = Math.max(
      1,
      Number(offer.buy_quantity || 1)
    );

    const boughtQty = activeCartItems
      .filter(
        (item) =>
          item.type !== "combo" &&
          item.type !== "addon_offer" &&
          Number(item.productId || item.id) ===
            buyProductId
      )
      .reduce(
        (sum, item) =>
          sum + Number(item.quantity || 0),
        0
      );

    if (boughtQty < requiredQty) {
      setErrorMessage(
        `Is offer ke liye required product quantity ${requiredQty} cart me add karein.`
      );
      return;
    }

    const cartKey = `buyxgety_${offerId}`;

    setCart((prev) => {
      if (
        prev.some(
          (item) =>
            String(item.cartKey || item.id) === cartKey
        )
      ) {
        return prev;
      }

      return [
        ...prev,
        {
          cartKey,
          type: "buy_x_get_y",
          offerType: "buy_x_get_y",
          offerId,
          productId: cartKey,
          id: cartKey,
          name:
            offer.offer_name ||
            offer.offer_text ||
            "Buy X Get Y",
          description:
            offer.offer_text ||
            `Buy ${offer.buy_quantity || 1}, Get ${
              offer.get_quantity || 1
            }`,
          selling_price: 0,
          price: 0,
          quantity: 1,
          isOfferItem: true,
        },
      ];
    });

    setMessage("Buy X Get Y offer selected.");
    setErrorMessage("");
  };

  const removeCartItemByKey = (item) => {
    const key = String(
      item.cartKey || item.id || item.productId || ""
    );

    setCart((prev) =>
      prev.filter(
        (cartItem) =>
          String(
            cartItem.cartKey ||
              cartItem.id ||
              cartItem.productId ||
              ""
          ) !== key
      )
    );
  };

  const buildOrderProductPayload = () => {
    return activeCartItems.map((item) => {
      const itemType = String(item.type || "product").toLowerCase();

      if (itemType === "combo") {
        return {
          type: "combo",
          offerType: "combo",
          offerId: Number(item.offerId || 0),
          offer_id: Number(item.offerId || 0),
          productId: `combo_${Number(item.offerId || 0)}`,
          id: `combo_${Number(item.offerId || 0)}`,
          name: item.name,
          quantity: Number(item.quantity || 1),
          price: Number(item.selling_price || item.price || 0),
          image: item.image || "",
        };
      }

      if (itemType === "addon_offer") {
        return {
          type: "addon_offer",
          offerType: "bill_unlock_addon",
          offerId: Number(item.offerId || 0),
          offer_id: Number(item.offerId || 0),
          productId: `addon_${Number(item.offerId || 0)}`,
          id: `addon_${Number(item.offerId || 0)}`,
          name: item.name,
          quantity: Number(item.quantity || 1),
          price: Number(item.selling_price || item.price || 0),
          image: item.image || "",
        };
      }

      if (itemType === "buy_x_get_y") {
        return {
          type: "buy_x_get_y",
          offerType: "buy_x_get_y",
          offerId: Number(item.offerId || 0),
          offer_id: Number(item.offerId || 0),
          productId: `buyxgety_${Number(item.offerId || 0)}`,
          id: `buyxgety_${Number(item.offerId || 0)}`,
          name: item.name,
          quantity: 1,
          price: 0,
          image: item.image || "",
        };
      }

      return {
        type: "product",
        productId: Number(item.productId || item.id),
        id: Number(item.productId || item.id),
        name: item.name,
        price: Number(item.selling_price || item.price || 0),
        selling_price: Number(item.selling_price || item.price || 0),
        quantity: Number(item.quantity || 0),
        image: item.image,
        category: item.category,
        food_type: item.food_type || "veg",
        description: item.description || "",
        line_total:
          Number(item.selling_price || item.price || 0) *
          Number(item.quantity || 0),
      };
    });
  };

  const buildDeliveryDetailsPayload = () => {
    const mobile = normalizeMobile(loginForm.mobile || customer?.mobile || customer?.customer_mobile || "");
    const name = loginForm.name || customer?.name || customer?.customer_name || "";

    if (orderMode !== "delivery") {
      return {
        customer_name: name || null,
        customer_mobile: mobile || null,
      };
    }

    return {
      customer_name: name || null,
      customer_mobile: mobile || null,
      address_label: addressForm.label || null,
      address_line1: addressForm.address_line1 || null,
      address_line2: addressForm.address_line2 || null,
      landmark: addressForm.landmark || null,
      city: addressForm.city || null,
      state: addressForm.state || null,
      pincode: addressForm.pincode || null,
      delivery_notes: addressForm.delivery_notes || null,
      latitude: addressForm.latitude ? Number(addressForm.latitude) : null,
      longitude: addressForm.longitude ? Number(addressForm.longitude) : null,
      customer_latitude: addressForm.latitude ? Number(addressForm.latitude) : null,
      customer_longitude: addressForm.longitude ? Number(addressForm.longitude) : null,
    };
  };

  const validateBeforeReview = () => {
    clearMessages();

    if (!customer && !normalizeMobile(loginForm.mobile)) {
      setErrorMessage("Order ke liye customer profile required hai.");
      setShowLoginCard(true);
      return false;
    }

    if (activeCartItems.length === 0) {
      setErrorMessage("Cart me kam se kam 1 item add karein.");
      return false;
    }

    if (currentMinOrder > 0 && baseTotal < currentMinOrder) {
      setErrorMessage(
        `Delivery ke liye minimum order ${formatMoney(currentMinOrder)} hai. Abhi ${formatMoney(
          baseTotal
        )} hai.`
      );
      return false;
    }

    return true;
  };

  const validateBeforePayment = () => {
    if (!validateBeforeReview()) return false;

    if (
      selectedCoinsToUse > 0 &&
      selectedCoinsToUse < coinRedeemConfig.minRedeem
    ) {
      window.alert(
        `Minimum ${coinRedeemConfig.minRedeem} coins use karna required hai.`
      );
      return false;
    }

    if (selectedCoinsToUse > maximumUsableCoins) {
      window.alert(
        `Is order par maximum ${maximumUsableCoins} coins use ho sakte hain.`
      );
      return false;
    }

    if (orderMode === "delivery") {
      if (!addressForm.address_line1 && !addressForm.latitude) {
        setErrorMessage("Delivery ke liye Profile Address ya Work Address required hai.");
        return false;
      }

      const latestCheck = checkDeliveryAvailability(addressForm);
      if (latestCheck.checked && latestCheck.allowed === false) {
        setErrorMessage(latestCheck.message || "Delivery is address par available nahi hai.");
        return false;
      }
    }

    return true;
  };

  const saveRedeemCode = async ({ orderId, token, redeemCode, expiryDate }) => {
    const response = await axios.post(`${apiUrl}/public/order/redeem/save`, {
      redeemCode,
      expiryDate,
      orderId,
      token,
      vendorId,
      vendorType: businessType,
    });

    return response.data;
  };

  const handlePlaceOrder = async () => {
    if (!validateBeforePayment()) return;

    if (paymentMode === "online") {
      setErrorMessage("Online payment abhi Food Radar order page me connect nahi hai. Cash/COD use karein.");
      return;
    }

    try {
      setPlacingOrder(true);
      clearMessages();

      const mobile = normalizeMobile(loginForm.mobile || customer?.mobile || customer?.customer_mobile || "");
      const name = loginForm.name || customer?.name || customer?.customer_name || "";
      const orderProducts = buildOrderProductPayload();

      const idempotencyKey = `food-radar-${vendorId}-${mobile}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;

      const validDiscountOffer =
        selectedBillDiscount &&
        Number(selectedBillDiscount.id || 0) > 0
          ? selectedBillDiscount
          : null;

      const resolvedFoodRadarCustomerId =
        getFoodRadarCustomerId(customer);

      if (!resolvedFoodRadarCustomerId) {
        setShowLoginCard(true);
        throw new Error(
          "Coins save karne ke liye Food Radar profile required hai. Profile dobara save karein."
        );
      }

      localStorage.setItem(
        "foodRadarCustomerId",
        String(resolvedFoodRadarCustomerId)
      );

      const payload = {
        vendorId: Number(vendorId),
        vendor_id: Number(vendorId),

        vendorType: businessType,
        businessType,
        userdb: true,

        customer_number: mobile,
        customer_mobile: mobile,
        customer_name: name || null,

        foodRadarCustomerId:
          resolvedFoodRadarCustomerId,
        food_radar_customer_id:
          resolvedFoodRadarCustomerId,
        customer_id:
          resolvedFoodRadarCustomerId,

        products: orderProducts,

        order_type: "individual",
        order_mode: orderMode,

        online_source: "food-radar",
        order_source: "food-radar",
        source: "food-radar",

        offer_name: validDiscountOffer
          ? validDiscountOffer.offer_name ||
            validDiscountOffer.offerName ||
            null
          : null,

        appliedOfferId: validDiscountOffer
          ? Number(validDiscountOffer.id)
          : null,

        applied_offer_id: validDiscountOffer
          ? Number(validDiscountOffer.id)
          : null,

        discountAmount: validDiscountOffer
          ? Number(discountAmount || 0)
          : 0,

        discount_amount: validDiscountOffer
          ? Number(discountAmount || 0)
          : 0,

        coinsToRedeem: appliedCoins,
        coins_to_redeem: appliedCoins,
        useCoins: appliedCoins > 0,
        use_coins: appliedCoins > 0,

        delivery_details: buildDeliveryDetailsPayload(),
        paymentReferenceId: null,
        idempotencyKey,
      };

      console.log("FOOD RADAR ORDER PAYLOAD:", payload);
      console.log("VALID DISCOUNT OFFER:", validDiscountOffer);

      const response = await axios.post(`${apiUrl}/public/order/place`, payload);

      const orderId =
        response.data?.orderId ||
        response.data?.insertId ||
        response.data?.order?.id ||
        response.data?.id;

      const fallbackToken = generateToken();
      const fallbackRedeemCode = generateRedeemCode();
      const expiryDate = getExpiryDateString();

      let redeemPayload = {
        token: fallbackToken,
        code: fallbackRedeemCode,
        redeemCode: fallbackRedeemCode,
        expiry_date: expiryDate,
        expiryDate,
      };

      if (orderId) {
        try {
          const redeemRes = await saveRedeemCode({
            orderId,
            token: fallbackToken,
            redeemCode: fallbackRedeemCode,
            expiryDate,
          });

          redeemPayload = {
            ...redeemPayload,
            ...redeemRes,
            token: redeemRes?.token || fallbackToken,
            code: redeemRes?.code || redeemRes?.redeemCode || fallbackRedeemCode,
            redeemCode: redeemRes?.redeemCode || redeemRes?.code || fallbackRedeemCode,
            expiry_date: redeemRes?.expiry_date || redeemRes?.expiryDate || expiryDate,
            expiryDate: redeemRes?.expiryDate || redeemRes?.expiry_date || expiryDate,
          };
        } catch {
          // Fallback token/redeem details will still be shown to customer.
        }
      }

      const finalOrder = {
        ...(response.data?.order || {}),
        id: orderId,
        order_id: orderId,
        products: orderProducts,
        productsParsed: orderProducts,
        customer_number: mobile,
        customer_name: name || null,
        customer_mobile: mobile,
        total_price: Number(response.data?.totalPrice || finalTotal || 0),
        order_mode: orderMode,
        status: "pending",
        payment_status: orderMode === "delivery" ? "cod" : "unpaid",
        payment_method: orderMode === "delivery" ? "cash" : "none",
        redeem: {
          token: redeemPayload.token,
          code: redeemPayload.code || redeemPayload.redeemCode,
          redeemCode: redeemPayload.redeemCode || redeemPayload.code,
          expiry_date: redeemPayload.expiry_date || redeemPayload.expiryDate,
        },
        token_number: redeemPayload.token,
        redeem_code: redeemPayload.code || redeemPayload.redeemCode,
        expiry_date: redeemPayload.expiry_date || redeemPayload.expiryDate,
        food_radar_customer_id:
          response.data?.order?.food_radar_customer_id ||
          response.data?.food_radar_customer_id ||
          resolvedFoodRadarCustomerId,
        coins_earned:
          Number(
            response.data?.order?.coins_earned ??
              response.data?.coins_earned ??
              response.data?.order?.coinsEarned ??
              0
          ) || 0,
        coin_earn_status:
          response.data?.order?.coin_earn_status ||
          response.data?.coin_earn_status ||
          "none",
        coins_redeemed:
          Number(
            response.data?.order?.coins_redeemed ??
              response.data?.coins_redeemed ??
              appliedCoins ??
              0
          ) || 0,
        coin_discount_amount:
          Number(
            response.data?.order?.coin_discount_amount ??
              response.data?.coin_discount_amount ??
              coinDiscountAmount ??
              0
          ) || 0,
        coin_redeem_status:
          response.data?.order?.coin_redeem_status ||
          response.data?.coin_redeem_status ||
          (appliedCoins > 0 ? "completed" : "none"),
      };

      setPlacedOrder(finalOrder);
      setStep(STEP_SUCCESS);
      setCart([]);
      setSelectedCoinsToUse(0);

      /*
        Notification conversion attribution:
        Order already successfully create ho chuka hai.
        Tracking fail ho to order success flow par koi effect nahi padega.
      */
      trackNotificationConversion({
        eventType: "order_placed",
        orderId: Number(orderId || finalOrder.id || 0) || null,
        orderAmount: Number(
          finalOrder.final_total ??
          finalOrder.total_amount ??
          finalTotal ??
          0
        ),
      });

      const wonCoins = Number(
        finalOrder.coins_earned || 0
      );

      if (wonCoins > 0) {
        setMessage("Order placed successfully.");
        setCoinRewardPopupValue(wonCoins);
        setShowCoinRewardPopup(true);
        playCoinKhanSound();
      } else {
        setMessage("Order placed successfully.");
        setCoinRewardPopupValue(0);
        setShowCoinRewardPopup(false);
        openRatingPopup();
      }
    } catch (error) {
      console.error("Food Radar place order error:", error);
      setErrorMessage(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.response?.data?.details ||
          error.message ||
          "Order place nahi hua."
      );
    } finally {
      setPlacingOrder(false);
    }
  };

  const goToReview = () => {
    if (!validateBeforeReview()) return;
    setStep(STEP_REVIEW);
  };

  const goToPayment = () => {
    if (!validateBeforePayment()) return;
    setStep(STEP_PAYMENT);
  };

  const renderHeader = () => (
    <header className="fro-header">
      <button type="button" className="fro-back-btn" onClick={() => navigate("/food-radar")}>
        ←
      </button>

      <div className="fro-shop-head">
        <div className="fro-shop-img">
          <img src={normalizeImage(shopImage)} alt={shopName} />
        </div>
        <div>
          <p>Food Radar Order</p>
          <h1>{shopName}</h1>
          <span>{getOrderModeLabel(orderMode)}</span>
        </div>
      </div>
    </header>
  );

  const renderMessages = () => (
    <>
      {message ? <div className="fro-message success">{message}</div> : null}
      {errorMessage ? <div className="fro-message error">{errorMessage}</div> : null}
    </>
  );

  const renderLoginCard = () => {
    if (!showLoginCard) return null;

    return (
      <section className="fro-card fro-login-card">
        <div className="fro-section-title-row">
          <div>
            <p>Customer Profile</p>
            <h2>Order continue karne ke liye profile save karein</h2>
          </div>
        </div>

        <form className="fro-login-form" onSubmit={handleSaveCustomer}>
          <label>
            Name
            <input
              type="text"
              placeholder="Your name"
              value={loginForm.name}
              onChange={(e) => setLoginForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </label>

          <label>
            Mobile Number *
            <input
              type="tel"
              placeholder="10 digit mobile number"
              value={loginForm.mobile}
              onChange={(e) =>
                setLoginForm((prev) => ({ ...prev, mobile: normalizeMobile(e.target.value) }))
              }
            />
          </label>

          <label>
            Email Optional
            <input
              type="email"
              placeholder="Email"
              value={loginForm.email}
              onChange={(e) => setLoginForm((prev) => ({ ...prev, email: e.target.value }))}
            />
          </label>

          <button type="submit" disabled={loginSaving}>
            {loginSaving ? "Saving..." : "Save & Continue"}
          </button>
        </form>
      </section>
    );
  };

  const goToDetailsFromMode = () => {
    clearMessages();

    if (
      orderMode === "dine_in" &&
      !dineInEnabled
    ) {
      setErrorMessage("Dine-in abhi available nahi hai.");
      return;
    }

    if (
      orderMode === "takeaway" &&
      !pickupEnabled
    ) {
      setErrorMessage("Takeaway abhi available nahi hai.");
      return;
    }

    if (
      orderMode === "delivery" &&
      !deliveryEnabled
    ) {
      setErrorMessage("Delivery abhi available nahi hai.");
      return;
    }

    if (!customer) {
      setShowLoginCard(true);
    }

    if (orderMode === "delivery") {
      selectProfileAddress();
    }

    setStep(STEP_DETAILS);
  };


  const goToProductsFromDetails = () => {
    clearMessages();

    const resolvedCustomerId =
      getFoodRadarCustomerId(customer);

    const mobile =
      normalizeMobile(
        loginForm.mobile ||
        customer?.mobile ||
        customer?.customer_mobile ||
        ""
      );

    const name =
      String(
        loginForm.name ||
        customer?.name ||
        customer?.customer_name ||
        ""
      ).trim();

    if (
      !resolvedCustomerId ||
      mobile.length !== 10
    ) {
      setShowLoginCard(true);
      setErrorMessage(
        "Menu continue karne se pehle customer profile aur valid 10 digit mobile save karein."
      );
      return;
    }

    if (
      (orderMode === "takeaway" ||
        orderMode === "delivery") &&
      !name
    ) {
      setShowLoginCard(true);
      setErrorMessage(
        `${orderMode === "delivery" ? "Delivery" : "Takeaway"} ke liye customer name required hai.`
      );
      return;
    }

    if (orderMode === "delivery") {
      if (
        !addressForm.address_line1 &&
        !addressForm.latitude
      ) {
        setErrorMessage(
          "Delivery ke liye Profile Address ya Work Address select karein."
        );
        return;
      }

      const latestCheck =
        checkDeliveryAvailability(addressForm);

      if (
        latestCheck.checked &&
        latestCheck.allowed === false
      ) {
        setErrorMessage(
          latestCheck.message ||
          "Delivery is address par available nahi hai."
        );
        return;
      }
    }

    setStep(STEP_PRODUCTS);
  };


  const renderStepTabs = () => (
    <div className="fro-step-tabs">
      <button className={step === STEP_MODE ? "active" : ""} type="button" onClick={() => setStep(STEP_MODE)}>
        1 Mode
      </button>
      <button className={step === STEP_DETAILS ? "active" : ""} type="button" onClick={() => setStep(STEP_DETAILS)}>
        2 Details
      </button>
      <button className={step === STEP_PRODUCTS ? "active" : ""} type="button" onClick={goToProductsFromDetails}>
        3 Menu
      </button>
      <button className={step === STEP_REVIEW ? "active" : ""} type="button" onClick={goToReview}>
        4 Review
      </button>
      <button className={step === STEP_PAYMENT ? "active" : ""} type="button" onClick={goToPayment}>
        5 Payment
      </button>
    </div>
  );

  const handleCategorySelect = (cat) => {
    setCategoryFilter(cat);
    setShowCategorySidebar(false);
    setStep(STEP_PRODUCTS);
  };

  const renderCategorySidebar = () => {
    if (step !== STEP_PRODUCTS || step === STEP_SUCCESS) return null;

    return (
      <>
        <button
          type="button"
          className="fro-category-float-btn"
          onClick={() => setShowCategorySidebar(true)}
          aria-label="Open product categories"
          title="Categories"
        >
          <span className="fro-category-chevrons" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        </button>

        <div
          className={`fro-category-sidebar-backdrop ${showCategorySidebar ? "open" : ""}`}
          onClick={() => setShowCategorySidebar(false)}
        />

        <aside className={`fro-category-sidebar ${showCategorySidebar ? "open" : ""}`}>
          <div className="fro-category-sidebar-head">
            <div>
              <p>Product Category</p>
              <h3>Select Category</h3>
            </div>
            <button type="button" onClick={() => setShowCategorySidebar(false)}>
              ×
            </button>
          </div>

          <div className="fro-category-sidebar-list">
            {categories.map((cat) => (
              <button
                type="button"
                key={cat}
                className={categoryFilter === cat ? "active" : ""}
                onClick={() => handleCategorySelect(cat)}
              >
                <span>{cat}</span>
                <b>{categoryCounts[cat] || 0}</b>
              </button>
            ))}
          </div>
        </aside>
      </>
    );
  };

  const renderModeStep = () => (
    <section className="fro-card fro-mode-card">
      <div className="fro-section-title-row">
        <div>
          <p>Step 1</p>
          <h2>Select Order Mode</h2>
        </div>
      </div>

      <div className="fro-mode-grid">
        <button
          type="button"
          disabled={!dineInEnabled}
          className={orderMode === "dine_in" ? "active" : ""}
          onClick={() => setOrderMode("dine_in")}
        >
          <span>🍽️</span>
          <strong>Dine-in</strong>
          <small>{dineInEnabled ? "Shop par baith kar order" : "Dine-in off hai"}</small>
        </button>

        <button
          type="button"
          disabled={!pickupEnabled}
          className={orderMode === "takeaway" ? "active" : ""}
          onClick={() => setOrderMode("takeaway")}
        >
          <span>🥡</span>
          <strong>Takeaway</strong>
          <small>{pickupEnabled ? "Shop se pickup karein" : "Takeaway off hai"}</small>
        </button>

        <button
          type="button"
          disabled={!deliveryEnabled}
          className={orderMode === "delivery" ? "active" : ""}
          onClick={() => {
            setOrderMode("delivery");
            selectProfileAddress();
          }}
        >
          <span>🛵</span>
          <strong>Delivery</strong>
          <small>{deliveryEnabled ? "Profile/Work address par delivery" : "Delivery off hai"}</small>
        </button>
      </div>

      <button className="fro-primary-btn" type="button" onClick={goToDetailsFromMode}>
        Continue to Details
      </button>
    </section>
  );

  const renderDetailsStep = () => {
    const customerMobile =
      normalizeMobile(
        loginForm.mobile ||
        customer?.mobile ||
        customer?.customer_mobile ||
        ""
      );

    const customerDisplayName =
      String(
        loginForm.name ||
        customer?.name ||
        customer?.customer_name ||
        ""
      ).trim();

    return (
      <section className="fro-card fro-mode-card">
        <div className="fro-section-title-row">
          <div>
            <p>Step 2</p>
            <h2>Customer & Order Details</h2>
          </div>
        </div>

        <div
          style={{
            padding: "12px",
            borderRadius: "12px",
            background: "#fff8e8",
            marginBottom: "14px",
          }}
        >
          <strong>
            Selected Mode: {getOrderModeLabel(orderMode)}
          </strong>

          <div style={{ marginTop: "6px", fontSize: "13px" }}>
            {customer
              ? `${customerDisplayName || "Customer"} • ${customerMobile || "Mobile unavailable"}`
              : "Customer profile save karna required hai."}
          </div>
        </div>

        {!customer ? (
          <div
            className="fro-message error"
            style={{ marginBottom: "14px" }}
          >
            Upar Customer Profile form me name/mobile save karke phir continue karein.
          </div>
        ) : null}

        {orderMode === "delivery" ? (
          renderAddressSection()
        ) : (
          <div
            style={{
              padding: "12px",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
              marginBottom: "14px",
            }}
          >
            <strong>
              {orderMode === "takeaway"
                ? "🥡 Takeaway / Parcel Details"
                : "🍽️ Dine-in Details"}
            </strong>

            <p
              style={{
                margin: "6px 0 0",
                fontSize: "13px",
                color: "#64748b",
              }}
            >
              {orderMode === "takeaway"
                ? "Order shop se pickup hoga. Customer name aur mobile confirm karke menu continue karein."
                : "Order dine-in ke liye place hoga. Customer details confirm karke menu continue karein."}
            </p>
          </div>
        )}

        <div className="fro-bottom-actions">
          <button
            type="button"
            className="fro-secondary-btn"
            onClick={() => setStep(STEP_MODE)}
          >
            Back
          </button>

          <button
            type="button"
            className="fro-primary-btn"
            onClick={goToProductsFromDetails}
          >
            Continue to Menu
          </button>
        </div>
      </section>
    );
  };


  const renderFoodRadarOffersPanel = () => {
    if (!normalizedOffers.length) return null;

    const renderSelectButton = (offer, label = "Apply Offer") => {
      const selected =
        Number(selectedBillOfferId) === Number(offer.id);
      const eligible = Boolean(offer.eligible);

      return (
        <button
          type="button"
          className={
            selected
              ? "fro-secondary-btn"
              : "fro-primary-btn"
          }
          disabled={!eligible}
          onClick={() =>
            setSelectedBillOfferId(
              selected ? null : Number(offer.id)
            )
          }
        >
          {selected
            ? "Remove Offer"
            : eligible
            ? label
            : "Locked"}
        </button>
      );
    };

    return (
      <div
        className="fro-offers-panel"
        style={{
          marginBottom: 18,
          padding: 14,
          border: "1px solid #f0d7a3",
          borderRadius: 16,
          background: "#fffaf0",
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <strong style={{ fontSize: 18 }}>
            🎁 Available Offers
          </strong>
          <div style={{ fontSize: 13, opacity: 0.75 }}>
            Vendor ke active offers yahan select karein.
          </div>
        </div>

        <div
          className="fro-offers-grid"
          style={{
            display: "grid",
            gap: 10,
            gridTemplateColumns:
              "repeat(auto-fit, minmax(230px, 1fr))",
          }}
        >
          {comboOffers.map((offer) => {
            const added = activeCartItems.some(
              (item) =>
                item.type === "combo" &&
                Number(item.offerId) ===
                  Number(offer.id)
            );

            return (
              <div
                className="fro-offer-card"
                key={`combo-${offer.id}`}
              >
                <small>COMBO OFFER</small>
                <h3>
                  {offer.offer_name ||
                    offer.combo_name ||
                    "Special Combo"}
                </h3>
                <p>
                  {safeArray(offer.combo_items)
                    .map(
                      (item) =>
                        `${item.name || "Item"} x${
                          item.quantity || item.qty || 1
                        }`
                    )
                    .join(" + ") ||
                    offer.offer_text}
                </p>
                <div className="fro-offer-price">
                  <b>{formatMoney(offer.combo_price)}</b>
                  {Number(offer.actual_price || 0) >
                  Number(offer.combo_price || 0) ? (
                    <del>
                      {formatMoney(offer.actual_price)}
                    </del>
                  ) : null}
                </div>
                <button
                  type="button"
                  className={
                    added
                      ? "fro-secondary-btn"
                      : "fro-primary-btn"
                  }
                  disabled={added}
                  onClick={() =>
                    addComboOfferToCart(offer)
                  }
                >
                  {added ? "Added" : "Add Combo"}
                </button>
              </div>
            );
          })}

          {addonOffers.map((offer) => {
            const minBill = Number(
              offer.min_bill_amount ||
                offer.min_purchasing_price ||
                0
            );
            const unlocked = baseTotal >= minBill;
            const added = activeCartItems.some(
              (item) =>
                item.type === "addon_offer" &&
                Number(item.offerId) ===
                  Number(offer.id)
            );

            return (
              <div
                className="fro-offer-card"
                key={`addon-${offer.id}`}
              >
                <small>
                  {unlocked
                    ? "ADD-ON UNLOCKED"
                    : "LOCKED ADD-ON"}
                </small>
                <h3>
                  {offer.offer_name ||
                    offer.addon_product_name ||
                    "Add-on Deal"}
                </h3>
                <p>
                  {offer.addon_product_name ||
                    "Special item"}{" "}
                  at{" "}
                  <b>
                    {formatMoney(
                      offer.addon_special_price
                    )}
                  </b>
                </p>
                <p>
                  {unlocked
                    ? `Bill ${formatMoney(minBill)}+ complete`
                    : `${formatMoney(
                        Math.max(0, minBill - baseTotal)
                      )} aur add karein`}
                </p>
                <button
                  type="button"
                  className={
                    unlocked && !added
                      ? "fro-primary-btn"
                      : "fro-secondary-btn"
                  }
                  disabled={!unlocked || added}
                  onClick={() =>
                    addAddonOfferToCart(offer)
                  }
                >
                  {added
                    ? "Added"
                    : unlocked
                    ? "Add Deal"
                    : "Locked"}
                </button>
              </div>
            );
          })}

          {buyXGetYOffers.map((offer) => {
            const buyProductId = Number(
              offer.buy_product_id || 0
            );
            const requiredQty = Math.max(
              1,
              Number(offer.buy_quantity || 1)
            );
            const boughtQty = activeCartItems
              .filter(
                (item) =>
                  item.type !== "combo" &&
                  item.type !== "addon_offer" &&
                  Number(item.productId || item.id) ===
                    buyProductId
              )
              .reduce(
                (sum, item) =>
                  sum + Number(item.quantity || 0),
                0
              );
            const eligible = boughtQty >= requiredQty;
            const added = activeCartItems.some(
              (item) =>
                item.type === "buy_x_get_y" &&
                Number(item.offerId) ===
                  Number(offer.id)
            );

            return (
              <div
                className="fro-offer-card"
                key={`buy-get-${offer.id}`}
              >
                <small>BUY X GET Y</small>
                <h3>
                  {offer.offer_name ||
                    offer.offer_text ||
                    "Buy X Get Y"}
                </h3>
                <p>
                  Buy {offer.buy_quantity || 1} item,
                  get {offer.get_quantity || 1} reward
                  {String(
                    offer.reward_type || "free"
                  ).toLowerCase() === "free"
                    ? " FREE"
                    : ""}
                </p>
                <p>
                  {eligible
                    ? "Required quantity complete"
                    : `${Math.max(
                        0,
                        requiredQty - boughtQty
                      )} quantity aur add karein`}
                </p>
                <button
                  type="button"
                  className={
                    eligible && !added
                      ? "fro-primary-btn"
                      : "fro-secondary-btn"
                  }
                  disabled={!eligible || added}
                  onClick={() =>
                    addBuyXGetYOfferToCart(offer)
                  }
                >
                  {added
                    ? "Selected"
                    : eligible
                    ? "Claim Offer"
                    : "Locked"}
                </button>
              </div>
            );
          })}

          {discountLikeOffers.map((offer) => {
            const minBill = Number(
              offer.min_bill_amount ||
                offer.min_purchasing_price ||
                0
            );
            const previewSaving =
              getOfferDiscountPreview(offer);
            const typeLabel =
              offer.offer_type === "time_slot"
                ? "HAPPY HOUR"
                : offer.offer_type ===
                  "progressive_unlock"
                ? "PROGRESSIVE OFFER"
                : "BILL DISCOUNT";

            const progressiveStep =
              offer.offer_type ===
              "progressive_unlock"
                ? offer.unlocked_step
                : null;

            return (
              <div
                className="fro-offer-card"
                key={`discount-like-${offer.id}`}
              >
                <small>{typeLabel}</small>
                <h3>
                  {offer.offer_name ||
                    offer.offer_text ||
                    "Special Offer"}
                </h3>

                {offer.offer_type === "time_slot" ? (
                  <p>
                    {String(
                      offer.start_time || ""
                    ).slice(0, 5)}{" "}
                    -{" "}
                    {String(
                      offer.end_time || ""
                    ).slice(0, 5)}
                  </p>
                ) : null}

                {offer.offer_type ===
                "progressive_unlock" ? (
                  <p>
                    {progressiveStep
                      ? `Unlocked: ${
                          progressiveStep.title ||
                          "Reward"
                        }`
                      : `Next reward ke liye minimum bill required`}
                  </p>
                ) : (
                  <p>
                    Minimum bill {formatMoney(minBill)}
                  </p>
                )}

                <p>
                  {offer.eligible
                    ? previewSaving > 0
                      ? `You save ${formatMoney(
                          previewSaving
                        )}`
                      : "Reward unlocked"
                    : offer.offer_type === "time_slot"
                    ? "Offer abhi active nahi hai"
                    : `${formatMoney(
                        Math.max(0, minBill - baseTotal)
                      )} aur add karein`}
                </p>

                {renderSelectButton(
                  offer,
                  offer.offer_type ===
                    "progressive_unlock"
                    ? "Use Reward"
                    : "Apply Offer"
                )}
              </div>
            );
          })}

          {coinRewardOffers.map((offer) => {
            const config =
              parseJsonMaybe(offer.offer_json, {}) || {};
            const earnPercent = Number(
              config.coin_earn_percent ||
                offer.coin_earn_percent ||
                offer.reward_value ||
                0
            );
            const minBill = Number(
              config.coin_min_bill_amount ||
                offer.min_bill_amount ||
                offer.min_purchasing_price ||
                0
            );
            const maxEarn = Number(
              config.coin_max_earn_per_order ||
                offer.coin_max_earn_per_order ||
                0
            );
            const eligibleAmount = Math.max(
              0,
              baseTotal - discountAmount
            );

            let previewCoins =
              eligibleAmount >= minBill
                ? Math.floor(
                    (eligibleAmount * earnPercent) / 100
                  )
                : 0;

            if (maxEarn > 0) {
              previewCoins = Math.min(
                previewCoins,
                maxEarn
              );
            }

            return (
              <div
                className="fro-offer-card"
                key={`coin-${offer.id}`}
              >
                <small>SHOP COINS</small>
                <h3>
                  🪙{" "}
                  {offer.offer_name ||
                    offer.offer_text ||
                    "Earn Shop Coins"}
                </h3>
                <p>
                  Bill ka {earnPercent}% coins
                  {maxEarn > 0
                    ? `, max ${maxEarn} coins/order`
                    : ""}
                </p>
                <p>
                  Minimum bill {formatMoney(minBill)}
                </p>
                <div
                  className="fro-offer-unlocked"
                  style={{ margin: "8px 0" }}
                >
                  {previewCoins > 0
                    ? `Is order par approx ${previewCoins} coins pending milenge`
                    : `${formatMoney(
                        Math.max(0, minBill - eligibleAmount)
                      )} aur add karein`}
                </div>
                <button
                  type="button"
                  className="fro-secondary-btn"
                  disabled
                >
                  Completion ke baad usable
                </button>
              </div>
            );
          })}

          {offersByType.other.map((offer) => (
            <div
              className="fro-offer-card"
              key={`other-${offer.id}`}
            >
              <small>
                {String(
                  offer.offer_type || "SPECIAL"
                )
                  .replaceAll("_", " ")
                  .toUpperCase()}
              </small>
              <h3>
                {offer.offer_name ||
                  offer.offer_text ||
                  "Special Offer"}
              </h3>
              <p>
                {offer.offer_text ||
                  "Vendor special offer"}
              </p>
              <button
                type="button"
                className="fro-secondary-btn"
                disabled
              >
                Details Only
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderProductsStep = () => (
    <section className="fro-card fro-products-card">
      <div className="fro-section-title-row">
        <div>
          <p>Step 3</p>
          <h2>Menu Items</h2>
        </div>
        <span className="fro-cart-pill">🛒 {cartCount}</span>
      </div>

      <div className="fro-search-box">
        <span>🔎</span>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search food item..."
        />
      </div>

      <div className="fro-category-row">
        {categories.map((cat) => (
          <button
            type="button"
            key={cat}
            className={categoryFilter === cat ? "active" : ""}
            onClick={() => setCategoryFilter(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {renderFoodRadarOffersPanel()}

      {Object.keys(groupedProducts).length === 0 ? (
        <div className="fro-empty-state">
          <h3>No items found</h3>
          <p>
            {products.length > 0
              ? "Search/filter change karein."
              : "Menu API se product load nahi hua. Vendor product active/stock check karein."}
          </p>
        </div>
      ) : (
        Object.entries(groupedProducts).map(([category, items]) => (
          <div className="fro-product-group" key={category}>
            <h3>{category}</h3>

            {items.map((product) => {
              const qty = Number(getCartItem(product.id)?.quantity || 0);
              const badge = getFoodBadge(product.food_type);

              return (
                <article className="fro-product-card" key={product.id}>
                  <img src={product.image_url} alt={product.name} />

                  <div className="fro-product-info">
                    <div>
                      <h4>{product.name}</h4>
                      <span className={`fro-food-badge ${badge.className}`}>{badge.label}</span>
                      {product.description ? <p>{product.description}</p> : null}
                    </div>

                    <div className="fro-product-bottom">
                      <strong>{formatMoney(product.selling_price || product.price)}</strong>

                      {qty > 0 ? (
                        <div className="fro-qty-control">
                          <button type="button" onClick={() => decreaseQty(product)}>
                            −
                          </button>
                          <span>{qty}</span>
                          <button type="button" onClick={() => addProductToCart(product)}>
                            +
                          </button>
                        </div>
                      ) : (
                        <button className="fro-add-btn" type="button" onClick={() => addProductToCart(product)}>
                          Add
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ))
      )}

      <div className="fro-bottom-actions">
        <button type="button" className="fro-secondary-btn" onClick={() => setStep(STEP_MODE)}>
          Back
        </button>
        <button type="button" className="fro-primary-btn" onClick={goToReview}>
          Review Order {cartCount ? `(${cartCount})` : ""}
        </button>
      </div>
    </section>
  );

  const renderSummaryBox = () => (
    <div className="fro-summary-box">
      <div>
        <span>Item Total</span>
        <strong>{formatMoney(baseTotal)}</strong>
      </div>

      {extraCharges > 0 ? (
        <div>
          <span>{orderMode === "delivery" ? "Delivery + Packaging" : "Packaging"}</span>
          <strong>{formatMoney(extraCharges)}</strong>
        </div>
      ) : null}

      {discountAmount > 0 ? (
        <div className="discount">
          <span>Discount</span>
          <strong>-{formatMoney(discountAmount)}</strong>
        </div>
      ) : null}

      {activeCoinRewardOffer ? (
        <div
          style={{
            padding: "12px",
            margin: "8px 0",
            border: "1px solid #ead9a4",
            borderRadius: "12px",
            background: "#fffaf0",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
            <span>Available Coins</span>
            <strong>{coinWalletLoading ? "..." : availableCoins}</strong>
          </div>

          <label style={{ display: "block", marginTop: "10px" }}>
            Use Coins
            <input
              type="number"
              min={0}
              step={1}
              value={selectedCoinsToUse}
              placeholder="Kitne coins use karna hai?"
              disabled={!coinRedeemEligible}
              onChange={(event) => {
                const nextValue = Math.max(
                  0,
                  Math.floor(Number(event.target.value || 0))
                );

                if (nextValue > maximumUsableCoins) {
                  window.alert(
                    `Is order par maximum ${maximumUsableCoins} coins use ho sakte hain.`
                  );
                  setSelectedCoinsToUse(maximumUsableCoins);
                  return;
                }

                setSelectedCoinsToUse(nextValue);
              }}
              style={{
                width: "100%",
                marginTop: "6px",
                padding: "10px",
                borderRadius: "9px",
                border: "1px solid #d8c48b",
              }}
            />
          </label>

          {amountBeforeCoins < coinRedeemConfig.minOrderForRedeem ? (
            <small style={{ display: "block", marginTop: "8px", color: "#b45309" }}>
              Coins use karne ke liye minimum order{" "}
              {formatMoney(coinRedeemConfig.minOrderForRedeem)} required hai.
            </small>
          ) : selectedCoinsToUse > 0 &&
            selectedCoinsToUse < coinRedeemConfig.minRedeem ? (
            <small style={{ display: "block", marginTop: "8px", color: "#b45309" }}>
              Minimum {coinRedeemConfig.minRedeem} coins use karna required hai.
            </small>
          ) : null}
        </div>
      ) : null}

      {coinDiscountAmount > 0 ? (
        <div className="discount">
          <span>Coin Discount ({appliedCoins} coins)</span>
          <strong>-{formatMoney(coinDiscountAmount)}</strong>
        </div>
      ) : null}

      <div className="total">
        <span>Grand Total</span>
        <strong>{formatMoney(finalTotal)}</strong>
      </div>
    </div>
  );

  const renderAddressSection = () => {
    if (orderMode !== "delivery") return null;

    const profileAddress = getCustomerProfileAddress(customer || {}, customerAddresses);
    const workAddress = getWorkAddress(customerAddresses);
    const profileText = profileAddress ? getAddressText(profileAddress) || "Profile saved address" : "Profile address save nahi hai";
    const workText = workAddress
      ? getAddressText(workAddress) || "Saved work address"
      : "Work address ke liye current location use hoga";

    return (
      <div className="fro-address-section">
        <div className="fro-section-title-row small">
          <div>
            <p>Delivery Address</p>
            <h2>Profile Address ya Work Address select karein</h2>
          </div>
        </div>

        <div className="fro-address-list two-options">
          <button
            type="button"
            className={selectedDeliveryOption === "profile" ? "active" : ""}
            onClick={() => selectProfileAddress()}
          >
            <strong>🏠 Profile Address</strong>
            <span>{profileText}</span>
          </button>

          <button
            type="button"
            className={selectedDeliveryOption === "work" ? "active" : ""}
            onClick={() => selectWorkAddress()}
          >
            <strong>🏢 Work Address</strong>
            <span>{workText}</span>
          </button>
        </div>

        {deliveryCheck.message ? (
          <div className={`fro-delivery-check ${deliveryCheck.allowed ? "success" : "error"}`}>
            {deliveryCheck.allowed ? "✅" : "⚠️"} {deliveryCheck.message}
          </div>
        ) : null}

        <div className="fro-address-form compact-readonly">
          <label>
            Selected Address
            <textarea
              value={getAddressText(addressForm)}
              readOnly
              placeholder="Profile Address ya Work Address select karein"
            />
          </label>

          <div className="fro-form-grid">
            <label>
              City
              <input value={addressForm.city} readOnly placeholder="City" />
            </label>

            <label>
              Pincode
              <input value={addressForm.pincode} readOnly placeholder="Pincode" />
            </label>
          </div>

          {addressForm.latitude && addressForm.longitude ? (
            <p className="fro-location-note">
              Location: {Number(addressForm.latitude).toFixed(5)}, {Number(addressForm.longitude).toFixed(5)}
            </p>
          ) : (
            <p className="fro-location-note warning">
              Is address me location pin nahi hai. Work Address select karke location allow karein.
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderReviewStep = () => (
    <section className="fro-card fro-review-card">
      <div className="fro-section-title-row">
        <div>
          <p>Step 4</p>
          <h2>Review Order</h2>
        </div>
      </div>

      <div className="fro-review-list">
        {activeCartItems.map((item) => (
          <div className="fro-review-item" key={item.productId || item.id}>
            <div className="fro-review-item-main">
              <strong>{item.name}</strong>
              <span>
                {formatMoney(item.selling_price || item.price)} × {item.quantity}
              </span>
              <b>{formatMoney(Number(item.selling_price || item.price || 0) * Number(item.quantity || 0))}</b>
            </div>

            <div className="fro-review-controls">
              {!item.isOfferItem ? (
                <div className="fro-review-qty-control">
                  <button
                    type="button"
                    onClick={() => decreaseQty(item)}
                    aria-label={`Decrease ${item.name}`}
                  >
                    −
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    type="button"
                    onClick={() => addProductToCart(item)}
                    aria-label={`Increase ${item.name}`}
                  >
                    +
                  </button>
                </div>
              ) : (
                <span style={{ fontSize: 12, fontWeight: 700 }}>
                  OFFER ITEM
                </span>
              )}

              <button
                type="button"
                className="fro-review-remove-btn"
                onClick={() =>
                  item.isOfferItem
                    ? removeCartItemByKey(item)
                    : upsertCartItem(item, 0)
                }
                aria-label={`Remove ${item.name}`}
                title="Remove item"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>

      {selectedBillDiscount ? (
        <div className="fro-offer-unlocked">
          🎁 {selectedBillDiscount.offer_name ||
            selectedBillDiscount.offerName ||
            "Discount"} applied — save {formatMoney(discountAmount)}
        </div>
      ) : null}

      {renderAddressSection()}
      {renderSummaryBox()}

      <div className="fro-bottom-actions">
        <button type="button" className="fro-secondary-btn" onClick={() => setStep(STEP_PRODUCTS)}>
          Back
        </button>
        <button type="button" className="fro-primary-btn" onClick={goToPayment}>
          Continue Payment
        </button>
      </div>
    </section>
  );

  const renderPaymentStep = () => (
    <section className="fro-card fro-payment-card">
      <div className="fro-section-title-row">
        <div>
          <p>Step 5</p>
          <h2>Payment</h2>
        </div>
      </div>

      <div className="fro-payment-grid">
        <button
          type="button"
          disabled={!codAllowed}
          className={paymentMode === "cash" ? "active" : ""}
          onClick={() => setPaymentMode("cash")}
        >
          <span>💵</span>
          <strong>{orderMode === "delivery" ? "Cash on Delivery" : "Pay at Counter"}</strong>
          <small>{codAllowed ? "Order place karein, payment baad me." : "Cash disabled hai."}</small>
        </button>

        <button
          type="button"
          disabled={!onlineAllowed}
          className={paymentMode === "online" ? "active" : ""}
          onClick={() => setPaymentMode("online")}
        >
          <span>💳</span>
          <strong>Online Payment</strong>
          <small>{onlineAllowed ? "Next phase me connect hoga." : "Online payment disabled hai."}</small>
        </button>
      </div>

      {renderSummaryBox()}

      <div className="fro-bottom-actions">
        <button type="button" className="fro-secondary-btn" onClick={() => setStep(STEP_REVIEW)}>
          Back
        </button>
        <button
          type="button"
          className="fro-primary-btn"
          onClick={handlePlaceOrder}
          disabled={placingOrder}
        >
          {placingOrder ? "Processing..." : `Place Order ${formatMoney(finalTotal)}`}
        </button>
      </div>
    </section>
  );

  const renderSuccessStep = () => (
    <section className="fro-card fro-success-card">
      <div className="fro-success-icon">✓</div>
      <h2>Order Placed Successfully!</h2>
      <p>{shopName} ko aapka order mil gaya hai.</p>

      <div className="fro-success-grid">
        <div>
          <span>Order ID</span>
          <strong>#{placedOrder?.id || placedOrder?.order_id || "-"}</strong>
        </div>
        <div>
          <span>Total</span>
          <strong>{formatMoney(placedOrder?.total_price || finalTotal)}</strong>
        </div>
        <div>
          <span>Token</span>
          <strong>{placedOrder?.token_number || placedOrder?.redeem?.token || "-"}</strong>
        </div>
        <div>
          <span>Redeem Code</span>
          <strong>{placedOrder?.redeem_code || placedOrder?.redeem?.code || "-"}</strong>
        </div>
      </div>

      <button type="button" className="fro-primary-btn" onClick={() => navigate("/food-radar")}>
        Back to Food Radar
      </button>
    </section>
  );

  const openRatingPopup = async () => {
    const finalCustomerId = getFoodRadarCustomerId(
      customer
    );

    if (!finalCustomerId || !vendorId) {
      setShowRatingPopup(false);
      return;
    }

    try {
      const response = await axios.get(
        `${apiUrl}/public/order/rating-status`,
        {
          params: {
            customer_id: finalCustomerId,
            food_radar_customer_id:
              finalCustomerId,
            vendor_id: Number(vendorId),
            vendor_type: businessType,
          },
        }
      );

      const shouldAsk =
        response.data?.should_ask_rating !== false;

      if (!shouldAsk) {
        setShowRatingPopup(false);
        return;
      }

      setRatingValue(0);
      setRatingHoverValue(0);
      setRatingReviewText("");
      setRatingMessage("");
      setShowRatingPopup(true);
    } catch (error) {
      console.error(
        "Food Radar rating status check error:",
        error
      );

      setRatingValue(0);
      setRatingHoverValue(0);
      setRatingReviewText("");
      setRatingMessage("");
      setShowRatingPopup(true);
    }
  };

  const closeCoinRewardPopup = () => {
    setShowCoinRewardPopup(false);
    openRatingPopup();
  };

  const skipRating = () => {
    setShowRatingPopup(false);
    setRatingValue(0);
    setRatingHoverValue(0);
    setRatingReviewText("");
    setRatingMessage("");
  };

  const submitFoodRadarRating = async () => {
    const finalRating = Number(ratingValue || 0);
    const finalOrderId = Number(
      placedOrder?.id ||
        placedOrder?.order_id ||
        0
    );
    const finalCustomerId = getFoodRadarCustomerId(
      customer
    );

    if (finalRating < 1 || finalRating > 5) {
      setRatingMessage("Please 1 se 5 star rating select karein.");
      return;
    }

    if (!finalOrderId) {
      setRatingMessage("Order ID missing hai.");
      return;
    }

    if (!finalCustomerId) {
      setRatingMessage("Customer ID missing hai.");
      return;
    }

    try {
      setRatingSaving(true);
      setRatingMessage("");

      const response = await axios.post(
        `${apiUrl}/public/order/rating`,
        {
          customer_id: finalCustomerId,
          food_radar_customer_id: finalCustomerId,
          vendor_id: Number(vendorId),
          vendor_type: businessType,
          order_id: finalOrderId,
          rating: finalRating,
          review_text: String(
            ratingReviewText || ""
          )
            .trim()
            .slice(0, 500),
        }
      );

      if (!response.data?.success) {
        throw new Error(
          response.data?.message ||
            "Rating save nahi hui."
        );
      }

      setRatingMessage("Thank you! Aapki rating save ho gayi.");

      window.setTimeout(() => {
        setShowRatingPopup(false);
        setRatingValue(0);
        setRatingHoverValue(0);
        setRatingReviewText("");
        setRatingMessage("");
      }, 900);
    } catch (error) {
      console.error(
        "Food Radar rating submit error:",
        error
      );

      setRatingMessage(
        error.response?.data?.message ||
          error.response?.data?.details ||
          error.message ||
          "Rating save nahi hui."
      );
    } finally {
      setRatingSaving(false);
    }
  };

  const renderCoinRewardPopup = () => {
    if (!showCoinRewardPopup || Number(coinRewardPopupValue || 0) <= 0) {
      return null;
    }

    return (
      <div
        className="fro-coin-win-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Coin reward"
      >
        <div className="fro-coin-win-card">
          <button
            type="button"
            className="fro-coin-win-close"
            onClick={closeCoinRewardPopup}
            aria-label="Close reward popup"
          >
            ×
          </button>

          <div className="fro-coin-stage" aria-hidden="true">
            <span className="fro-flying-coin coin-a">₹</span>
            <span className="fro-flying-coin coin-b">₹</span>
            <span className="fro-flying-coin coin-c">₹</span>
            <span className="fro-flying-coin coin-d">₹</span>
            <span className="fro-flying-coin coin-e">₹</span>
            <span className="fro-flying-coin coin-f">₹</span>

            <div className="fro-main-coin">
              <div className="fro-main-coin-face">₹</div>
            </div>

            <span className="fro-coin-spark spark-a">✦</span>
            <span className="fro-coin-spark spark-b">✦</span>
            <span className="fro-coin-spark spark-c">✦</span>
            <span className="fro-coin-spark spark-d">✦</span>
          </div>

          <p className="fro-coin-win-kicker">CONGRATULATIONS!</p>

          <div className="fro-coin-win-amount">
            +{Number(coinRewardPopupValue || 0)}
          </div>

          <h3>Shop Coins</h3>

          <p className="fro-coin-win-note">
            Aapke coins abhi pending hain.
            <br />
            Order complete hone ke baad usable honge.
          </p>

          <button
            type="button"
            className="fro-coin-win-button"
            onClick={closeCoinRewardPopup}
          >
            Awesome!
          </button>
        </div>
      </div>
    );
  };

  const renderRatingPopup = () => {
    if (!showRatingPopup) return null;

    const activeRating =
      ratingHoverValue || ratingValue;

    const ratingLabels = {
      1: "Poor",
      2: "Needs Improvement",
      3: "Good",
      4: "Very Good",
      5: "Excellent",
    };

    return (
      <div
        className="fro-rating-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Rate your order"
      >
        <div className="fro-rating-card">
          <button
            type="button"
            className="fro-rating-close"
            onClick={skipRating}
            aria-label="Skip rating"
          >
            ×
          </button>

          <div className="fro-rating-icon" aria-hidden="true">
            ★
          </div>

          <p className="fro-rating-kicker">
            YOUR FEEDBACK MATTERS
          </p>

          <h3>How was your experience?</h3>

          <p className="fro-rating-subtitle">
            Is order aur shop ko 1 se 5 stars dein.
          </p>

          <div
            className="fro-rating-stars"
            onMouseLeave={() =>
              setRatingHoverValue(0)
            }
          >
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className={
                  star <= activeRating
                    ? "active"
                    : ""
                }
                onMouseEnter={() =>
                  setRatingHoverValue(star)
                }
                onClick={() => {
                  setRatingValue(star);
                  setRatingMessage("");
                }}
                aria-label={`${star} star rating`}
              >
                ★
              </button>
            ))}
          </div>

          <div className="fro-rating-label">
            {activeRating > 0
              ? ratingLabels[activeRating]
              : "Select your rating"}
          </div>

          <textarea
            className="fro-rating-review"
            value={ratingReviewText}
            onChange={(event) =>
              setRatingReviewText(
                event.target.value.slice(0, 500)
              )
            }
            placeholder="Review likhna optional hai..."
            maxLength={500}
          />

          <div className="fro-rating-count">
            {ratingReviewText.length}/500
          </div>

          {ratingMessage ? (
            <div
              className={`fro-rating-message ${
                ratingMessage
                  .toLowerCase()
                  .includes("thank you")
                  ? "success"
                  : "error"
              }`}
            >
              {ratingMessage}
            </div>
          ) : null}

          <button
            type="button"
            className="fro-rating-submit"
            onClick={submitFoodRadarRating}
            disabled={
              ratingSaving ||
              Number(ratingValue || 0) < 1
            }
          >
            {ratingSaving
              ? "Saving..."
              : "Submit Rating"}
          </button>

          <button
            type="button"
            className="fro-rating-skip"
            onClick={skipRating}
            disabled={ratingSaving}
          >
            Skip for now
          </button>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    if (step === STEP_MODE) return renderModeStep();
    if (step === STEP_DETAILS) return renderDetailsStep();
    if (step === STEP_PRODUCTS) return renderProductsStep();
    if (step === STEP_REVIEW) return renderReviewStep();
    if (step === STEP_PAYMENT) return renderPaymentStep();
    if (step === STEP_SUCCESS) return renderSuccessStep();
    return renderModeStep();
  };

  if (loading) {
    return (
      <div className="food-radar-order-page">
        <div className="fro-loading-card">
          <div className="fro-spinner" />
          <h2>Loading order page...</h2>
          <p>Vendor menu aur customer profile load ho raha hai.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="food-radar-order-page">
      {renderHeader()}

      <main className="fro-main">
        {renderMessages()}
        {renderLoginCard()}
        {step !== STEP_SUCCESS ? renderStepTabs() : null}
        {renderCurrentStep()}
      </main>

      {renderCategorySidebar()}
      {renderCoinRewardPopup()}
      {renderRatingPopup()}

      {step === STEP_PRODUCTS && cartCount > 0 ? (
        <div className="fro-floating-cart">
          <div>
            <strong>{cartCount} items</strong>
            <span>{formatMoney(finalTotal)}</span>
          </div>
          <button type="button" onClick={goToReview}>
            View Cart
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default FoodRadarOrderForm;
